import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
  CodeOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SaveOutlined,
  CloudUploadOutlined,
  ExportOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  HddOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TagOutlined,
  LinkOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { servicesService, ordersService } from '../../services';
import { ServiceEvent, ServiceEventResponse, AppLogsResponse, CustomDomain, mergeCustomDomain } from '../../services/services.service';
import { AppDeployService } from '../../types/service.type';
import { ServiceStatusBadge, ConfirmationModal, ServiceEventsList, DetailPageHeader, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { getTechnologyImage } from '../../utils/serviceIconMapper';
import { useNotificationContext } from '../../contexts/NotificationContext';
import MonitoringTab from '../ServiceDetail/components/MonitoringTab';
import BackupsTab from '../../components/shared/BackupsTab';
import LazyServiceEventsList from '../../components/shared/LazyServiceEventsList';
import { useAuth } from '../../contexts/AuthContext';
import { DeployRecord } from '../../types/deploys.type';
import DeployReleaseDeleteModal from '../../components/DeployReleaseDeleteModal';
import DeployStorageWarning from '../../components/DeployStorageWarning';

/**
 * Mapea el level a un tipo de evento para la UI
 */
const mapLevelToEventType = (level?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!level) return 'info';

  const levelUpper = level.toUpperCase();
  switch (levelUpper) {
    case 'SUCCESS':
      return 'success';
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    case 'INFO':
    default:
      return 'info';
  }
};

/**
 * Mapea el stackStatus a un tipo de evento para la UI (fallback si no hay level)
 */
const mapStackStatusToEventType = (stackStatus?: string): 'info' | 'warning' | 'error' | 'success' => {
  if (!stackStatus) return 'info';

  const status = stackStatus.toUpperCase();
  if (status.includes('COMPLETE') && !status.includes('FAILED')) {
    return 'success';
  }
  if (status.includes('FAILED') || status.includes('ERROR') || status.includes('ROLLBACK')) {
    return 'error';
  }
  if (status.includes('IN_PROGRESS') || status.includes('PROGRESS')) {
    return 'info';
  }
  return 'info';
};

/**
 * Normaliza el mensaje del evento
 */
const normalizeEventMessage = (message: string, stackStatus?: string): string => {
  if (!message) return 'No message';

  const messageLower = message.toLowerCase();
  const statusLower = stackStatus?.toLowerCase() || '';

  if (messageLower.includes('stacking') || statusLower.includes('stacking')) {
    return message.replace(/stacking/gi, 'Creating').trim();
  }

  if (messageLower.includes('creating')) {
    return message.replace(/creating/gi, 'Creating').trim();
  }

  return message;
};

/**
 * Convierte eventos del API al formato de la UI (mismas reglas que VMDetail y DatabaseDetail)
 */
const mapServiceEventsToUI = (events: ServiceEventResponse[]): ServiceEvent[] => {
  return events.map((event, index) => {
    let timestamp: Date;
    try {
      timestamp = new Date(event.createdAt);
    } catch {
      timestamp = new Date();
    }

    const eventType = event.level
      ? mapLevelToEventType(event.level)
      : mapStackStatusToEventType(event.stackStatus);

    const message = normalizeEventMessage(event.message, event.stackStatus);

    const isKubiyEvent = event.source?.toLowerCase() === 'kubiy';
    const messageLower = message.toLowerCase();
    const hasDeletionKeywords =
      messageLower.includes('deleted') ||
      messageLower.includes('eliminado') ||
      messageLower.includes('service deleted') ||
      messageLower.includes('servicio eliminado');
    const stackStatusLower = event.stackStatus?.toLowerCase() || '';
    const levelUpper = event.level?.toUpperCase() || '';
    const hasDeleteCompleteStatus =
      stackStatusLower.includes('delete_complete') ||
      stackStatusLower.includes('delete_completed') ||
      stackStatusLower.includes('deleted');
    const isSuccessfulDeletion = levelUpper === 'SUCCESS' && hasDeletionKeywords;
    const isDeletionEvent =
      (isKubiyEvent && hasDeletionKeywords) || hasDeleteCompleteStatus || isSuccessfulDeletion;

    return {
      id: event.PK || event.SK || `event-${index}`,
      timestamp,
      type: eventType,
      message,
      details: event.description || event.statusReason || undefined,
      source: event.source || 'System',
      isKubiyEvent,
      isDeletionEvent,
    };
  });
};

/**
 * Verifica si un servicio es de tipo APP_DEPLOY
 */
const isAppDeployServiceType = (type: string | undefined): boolean => {
  if (!type) return false;
  const normalizedType = type.toUpperCase();
  return normalizedType === 'APP_DEPLOY' ||
    normalizedType === 'APP_DEPLOY_STATIC' ||
    normalizedType === 'APP_DEPLOY_SERVER' ||
    normalizedType.startsWith('APP_DEPLOY');
};

/**
 * Verifica si el servicio está en proceso de creación
 */
const isServiceCreating = (status: string | undefined): boolean => {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return normalizedStatus === 'deploying' ||
    normalizedStatus === 'initializing' ||
    normalizedStatus === 'stacking' ||
    normalizedStatus === 'creating';
};

/**
 * Verifica si el servicio está en proceso de eliminación o ya fue eliminado
 */
const isServiceDeleting = (status: string | undefined): boolean => {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return normalizedStatus === 'deleting';
};

const isServiceDeleted = (status: string | undefined): boolean => {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return normalizedStatus === 'deleted' || normalizedStatus === 'terminated';
};

/**
 * Normaliza un dominio: lowercase, sin espacios, sin punto final.
 * No quita protocolos, eso lo valida `validateCustomDomain`.
 */
const normalizeDomain = (raw: string): string => {
  return raw.trim().toLowerCase().replace(/\.+$/, '');
};

/**
 * Valida un dominio personalizado. Devuelve `null` si es válido o una clave de i18n
 * (sin prefijo `deployApp.detail.`) con el mensaje de error correspondiente.
 *
 * Reglas:
 *  - No vacío
 *  - Sin protocolos (http://, https://)
 *  - Sin rutas ni puertos (/, :)
 *  - Sin espacios
 *  - Sin comodines (*)
 *  - No puede ser una IP
 *  - Longitud total <= 253
 *  - Al menos 2 labels (FQDN)
 *  - Cada label: 1-63 chars, alfanumérico y guiones, sin guion al inicio/final
 *  - TLD final: solo letras, 2-63 chars (acepta IDN punycode `xn--...`)
 */
const validateCustomDomain = (raw: string, existingDomains: string[] = []): string | null => {
  const value = (raw || '').trim();
  if (!value) return 'domainRequired';

  if (/\s/.test(value)) return 'domainNoSpaces';
  if (/^https?:\/\//i.test(value)) return 'domainNoProtocol';
  if (value.includes('/') || value.includes(':')) return 'domainNoPath';
  if (value.includes('*')) return 'domainNoWildcard';

  const domain = normalizeDomain(value);

  if (domain.length > 253) return 'domainTooLong';

  // Detectar IPv4 simple
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(domain)) return 'domainNoIp';

  const labels = domain.split('.');
  if (labels.length < 2) return 'domainNeedsTld';

  const labelRegex = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  for (const label of labels) {
    if (label.length === 0) return 'domainInvalid';
    if (label.length > 63) return 'domainLabelTooLong';
    if (!labelRegex.test(label)) return 'domainInvalidLabel';
  }

  const tld = labels[labels.length - 1];
  if (!/^[a-z]{2,63}$/.test(tld)) return 'domainNeedsTld';

  if (existingDomains.some((d) => normalizeDomain(d) === domain)) {
    return 'domainDuplicate';
  }

  return null;
};

const EC2_SPECS: Record<string, { vcpu: number; memoryMB: number }> = {
  't3.nano':    { vcpu: 2, memoryMB: 512 },
  't3.micro':   { vcpu: 2, memoryMB: 1024 },
  't3.small':   { vcpu: 2, memoryMB: 2048 },
  't3.medium':  { vcpu: 2, memoryMB: 4096 },
  't3.large':   { vcpu: 2, memoryMB: 8192 },
  't3.xlarge':  { vcpu: 4, memoryMB: 16384 },
  't3.2xlarge': { vcpu: 8, memoryMB: 32768 },
};

function formatMemory(mb: number): string {
  return mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`;
}

function formatRegion(region: string | undefined): string {
  if (!region) return 'N/A';
  const REGION_NAMES: Record<string, string> = {
    'us-east-1': 'US East (N. Virginia)',
    'us-west-2': 'US West (Oregon)',
    'eu-west-1': 'EU (Irlanda)',
    'ap-southeast-1': 'Asia Pacífico (Singapur)',
  };
  return REGION_NAMES[region] ?? region;
}

function formatTechnology(tech: string): string {
  const TECH_LABELS: Record<string, string> = {
    'nodejs': 'Node.js', 'node': 'Node.js', 'node.js': 'Node.js',
    'python': 'Python', 'python3': 'Python',
    'golang': 'Go', 'go': 'Go',
    'dotnet': '.NET', 'netcore': '.NET', '.net': '.NET',
    'static': 'Sitio Estático', 'html': 'HTML/CSS', 'react': 'React', 'vue': 'Vue.js',
    'ai-generated': 'IA Generada',
  };
  return TECH_LABELS[tech?.toLowerCase()] ?? tech;
}

const DeployAppDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [service, setService] = useState<AppDeployService | null>(null);
  const [product, setProduct] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<ServiceEvent[]>([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [isRefreshingEvents, setIsRefreshingEvents] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSecurityHelpModal, setShowSecurityHelpModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'apps' | 'events' | 'monitoring' | 'backups'>('overview');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [activeAppTab, setActiveAppTab] = useState<'overview' | 'configuration' | 'deploys' | 'monitoring'>('overview');
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [isGeneratingApiKey, setIsGeneratingApiKey] = useState(false);
  const [isRevokingApiKey, setIsRevokingApiKey] = useState<string | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [apiKeyToRevoke, setApiKeyToRevoke] = useState<string | null>(null);
  const [showApiKeyNameModal, setShowApiKeyNameModal] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyExpirationDays, setApiKeyExpirationDays] = useState<string>('');
  const [apiKeyUnlimited, setApiKeyUnlimited] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string>('');
  const [generatedTokenInfo, setGeneratedTokenInfo] = useState<any>(null);
  const [environmentVariables, setEnvironmentVariables] = useState<Array<{ name: string; value: string; hidden: boolean }>>([]);
  const [isEditingEnvVars, setIsEditingEnvVars] = useState(false);
  const [isSavingEnvVars, setIsSavingEnvVars] = useState(false);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isUploadingZip, setIsUploadingZip] = useState(false);
  const [zipDragActive, setZipDragActive] = useState(false);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const hasFetchedRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [githubConnected, setGithubConnected] = useState(false);
  const [githubRepos, setGithubRepos] = useState<any[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<any>(null);
  const [selectedBranch, setSelectedBranch] = useState('main');
  const [isConnectingGithub, setIsConnectingGithub] = useState(false);
  const [isConfiguringDeploy, setIsConfiguringDeploy] = useState(false);
  const [showGithubModal, setShowGithubModal] = useState(false);
  const [githubConfig, setGithubConfig] = useState<any>(null);
  const [isLoadingGithubConfig, setIsLoadingGithubConfig] = useState(false);
  const [deployStatus, setDeployStatus] = useState<{
    status: 'success' | 'failed' | 'pending' | 'in_progress' | 'no_deploys' | 'unknown';
    lastDeploy?: any;
  }>({ status: 'no_deploys' });
  const [isCheckingDeployStatus, setIsCheckingDeployStatus] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [githubStartMode, setGithubStartMode] = useState('npm');
  const [githubTargetMode, setGithubTargetMode] = useState('start');
  // Estados para agregar app
  const [showAddAppModal, setShowAddAppModal] = useState(false);
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const [configMountReady, setConfigMountReady] = useState(false);
  const configMountRef = useRef<HTMLDivElement>(null);
  const [configSubTab, setConfigSubTab] = useState<'connectivity' | 'apiKeys' | 'envVars' | 'domains'>('envVars');
  const [deployHistory, setDeployHistory] = useState<DeployRecord[]>([]);
  const [deployHistoryNextToken, setDeployHistoryNextToken] = useState<string | undefined>();
  const [isLoadingDeployHistory, setIsLoadingDeployHistory] = useState(false);
  const [hasMoreDeploys, setHasMoreDeploys] = useState(false);
  const [rollingBackDeployId, setRollingBackDeployId] = useState<string | null>(null);
  const [showRestartAppModal, setShowRestartAppModal] = useState(false);
  const [isRestartingApp, setIsRestartingApp] = useState(false);
  const [showStopAppModal, setShowStopAppModal] = useState(false);
  const [isStoppingApp, setIsStoppingApp] = useState(false);
  const [isStartingApp, setIsStartingApp] = useState(false);

  const handleRestartApp = async () => {
    if (!selectedAppId) return;
    try {
      setIsRestartingApp(true);
      const res = await servicesService.appProcessAction(selectedAppId, 'restart');
      if (res?.success) {
        success(t('deployApp.detail.restartAppSuccess'));
      } else {
        showError(t('deployApp.detail.restartAppError'));
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 429) {
        showError(t('deployApp.detail.restartAppRateLimited'));
      } else {
        showError(t('deployApp.detail.restartAppError'));
      }
    } finally {
      setIsRestartingApp(false);
      setShowRestartAppModal(false);
    }
  };

  const handleStopApp = async () => {
    if (!selectedAppId) return;
    try {
      setIsStoppingApp(true);
      const res = await servicesService.appProcessAction(selectedAppId, 'stop');
      if (res?.success) {
        success(t('deployApp.detail.stopAppSuccess'));
        handleRefresh();
      } else {
        showError(t('deployApp.detail.stopAppError'));
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 429) {
        showError(t('deployApp.detail.restartAppRateLimited'));
      } else {
        showError(t('deployApp.detail.stopAppError'));
      }
    } finally {
      setIsStoppingApp(false);
      setShowStopAppModal(false);
    }
  };

  const handleStartApp = async () => {
    if (!selectedAppId) return;
    try {
      setIsStartingApp(true);
      const res = await servicesService.appProcessAction(selectedAppId, 'start');
      if (res?.success) {
        success(t('deployApp.detail.startAppSuccess'));
        handleRefresh();
      } else {
        showError(t('deployApp.detail.startAppError'));
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 429) {
        showError(t('deployApp.detail.restartAppRateLimited'));
      } else {
        showError(t('deployApp.detail.startAppError'));
      }
    } finally {
      setIsStartingApp(false);
    }
  };
  const [appLogs, setAppLogs] = useState<string>('');
  const [appLogsError, setAppLogsError] = useState<string>('');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logType, setLogType] = useState<'deploy' | 'app' | 'app-error'>('deploy');
  const [logLines, setLogLines] = useState<number>(200);
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const [newAppData, setNewAppData] = useState({
    name: '',
    description: '',
    slug: '',
    environment: '',
    startMode: '',
    targetMode: '',
  });
  const { user } = useAuth();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'requesting' | 'uploading' | 'done' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string>('');
  // ============================================================
  // Storage Limit Modal (MAX_RELEASES_REACHED)
  // ============================================================
  const [showStorageLimitModal, setShowStorageLimitModal] = useState(false);
  const [storageLimitData, setStorageLimitData] = useState<{
    currentReleases?: number;
    maxReleases?: number;
    oldestRelease?: {
      deployId: string;
      createdAt: string;
      sizeBytes: number;
    };
  }>({});
  const [retryUploadAfterDelete, setRetryUploadAfterDelete] = useState(false);

  // ============================================================
  // Storage Warning Banner (LIVE_STORAGE_EXCEEDED)
  // ============================================================
  const [showStorageWarning, setShowStorageWarning] = useState(false);
  const [storageWarningData, setStorageWarningData] = useState<{
    siteSize?: number;
    storageLimit?: number;
  }>({});

  // ============================================================
  // Custom Domains (Server App Deploy)
  // ============================================================
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [newDomainInput, setNewDomainInput] = useState('');
  const [domainInputError, setDomainInputError] = useState<string | null>(null);
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [showDeleteDomainModal, setShowDeleteDomainModal] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<CustomDomain | null>(null);
  const [isDeletingDomain, setIsDeletingDomain] = useState(false);
  const [verifyingDomainId, setVerifyingDomainId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // ============================================================
  // Menú de acciones peligrosas (···)
  // ============================================================
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  // ============================================================
  // Plan change
  // ============================================================
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState('');
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [changePlanError, setChangePlanError] = useState<string | null>(null);

  // Cierra el menú ··· al hacer click fuera
  useEffect(() => {
    if (!showActionsMenu) return;
    const handler = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showActionsMenu]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('No se proporcionó un ID de servicio');
        setLoading(false);
        return;
      }

      if (hasFetchedRef.current === id) {
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      hasFetchedRef.current = id;

      try {
        setLoading(true);
        setError(null);

        const serviceData: any = await servicesService.getById(id);

        if (abortController.signal.aborted) return;

        if (!isAppDeployServiceType(serviceData.type)) {
          setError('Este servicio no es una aplicación deploy');
          setLoading(false);
          return;
        }

        // Guardar información del producto si viene en la respuesta
        if (serviceData.product) {
          setProduct(serviceData.product);
        }

        // Intentar obtener la orden si hay orderId
        if (serviceData.orderId) {
          try {
            const orderData = await ordersService.getById(serviceData.orderId);
            setOrder(orderData);
          } catch (err) {
            console.warn('Error loading order:', err);
          }
        }

        const normalizedStatus = serviceData.status?.toLowerCase() || serviceData.status;

        // Convertir fechas a Date si son strings
        const createdAt = serviceData.createdAt
          ? (typeof serviceData.createdAt === 'string' ? new Date(serviceData.createdAt) : serviceData.createdAt)
          : new Date();
        const updatedAt = serviceData.updatedAt
          ? (typeof serviceData.updatedAt === 'string' ? new Date(serviceData.updatedAt) : serviceData.updatedAt)
          : new Date();

        const mappedService: AppDeployService = {
          ...serviceData,
          type: 'APP_DEPLOY',
          status: normalizedStatus as any,
          publicUrl: serviceData.publicUrl,
          metadata: serviceData.metadata || {},
          apps: serviceData.apps || [],
          apiKeys: serviceData.apiKeys || [],
          createdAt,
          updatedAt,
        };

        setService(mappedService);

        // Seleccionar la primera app por defecto si hay apps
        if (serviceData.apps && serviceData.apps.length > 0) {
          setSelectedAppId(serviceData.apps[0].id);
        } else {
          setSelectedAppId(null);
        }

      } catch (err: any) {
        if (abortController.signal.aborted) return;
        console.error('Error fetching service:', err);
        setError(err.message || 'Error al cargar el servicio');
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id]);

  // Polling para servicios en creación
  useEffect(() => {
    if (!id || !service) return;

    if (!isServiceCreating(service.status)) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const serviceData: any = await servicesService.getById(id);

        if (serviceData && isAppDeployServiceType(serviceData.type)) {
          const normalizedStatus = serviceData.status?.toLowerCase() || serviceData.status;

          // Convertir fechas a Date si son strings
          const createdAt = serviceData.createdAt
            ? (typeof serviceData.createdAt === 'string' ? new Date(serviceData.createdAt) : serviceData.createdAt)
            : new Date();
          const updatedAt = serviceData.updatedAt
            ? (typeof serviceData.updatedAt === 'string' ? new Date(serviceData.updatedAt) : serviceData.updatedAt)
            : new Date();

          const mappedService: AppDeployService = {
            ...serviceData,
            type: 'APP_DEPLOY',
            status: normalizedStatus as any,
            publicUrl: serviceData.publicUrl,
            metadata: serviceData.metadata || {},
            createdAt,
            updatedAt,
          };

          setService(mappedService);

          // Cargar información de la app y variables de entorno desde el API de apps
          if (serviceData.apps && serviceData.apps.length > 0) {
            try {
              const appId = serviceData.apps[0].id;
              const appInfo = await servicesService.getAppById(appId);
              // Cargar variables de entorno desde envVars del API de apps
              if (appInfo.envVars && Array.isArray(appInfo.envVars)) {
                setEnvironmentVariables([...appInfo.envVars]);
              } else {
                setEnvironmentVariables([]);
              }
            } catch (err) {
              console.warn('Error loading app info in polling:', err);
              // Si no se puede obtener la app, dejar las variables vacías
              setEnvironmentVariables([]);
            }
          } else {
            // Si no hay apps, dejar las variables vacías
            setEnvironmentVariables([]);
          }

          // Si ya no está creando, detener el polling
          if (!isServiceCreating(normalizedStatus)) {
            clearInterval(interval);
            // Recargar eventos
            try {
              const eventsData = await servicesService.getEvents(id);
              const mappedEvents = mapServiceEventsToUI(eventsData);
              setEvents(mappedEvents);
            } catch (err) {
              console.error('Error loading events:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error polling service:', err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [id, service?.status]);

  // Cargar variables de entorno y API keys de la app seleccionada
  useEffect(() => {
    if (!id || !selectedAppId) {
      setEnvironmentVariables([]);
      setApiKeys([]);
      return;
    }

    const loadAppData = async () => {
      try {
        const appInfo = await servicesService.getAppById(selectedAppId);
        if (appInfo.envVars && Array.isArray(appInfo.envVars)) {
          setEnvironmentVariables([...appInfo.envVars]);
        } else {
          setEnvironmentVariables([]);
        }

        const response: any = await servicesService.getApiKeys(selectedAppId);
        let items: any[] = [];
        if (Array.isArray(response)) {
          items = response;
        } else if (response?.items && Array.isArray(response.items)) {
          items = response.items;
        } else if (response?.data && Array.isArray(response.data)) {
          items = response.data;
        } else if (response?.tokens && Array.isArray(response.tokens)) {
          items = response.tokens;
        }
        const mappedKeys = items.map((item: any) => ({
          token: item.token || '',
          tokenInfo: {
            tenantId: item.tenantId,
            serviceId: item.serviceId,
            appId: item.appId,
            tokenId: item.tokenId,
            name: item.name,
            scope: item.scope,
            status: item.status || 'active',
            createdAt: item.createdAt,
            lastUsedAt: item.lastUsedAt,
            expiresAt: item.expiresAt,
            ...(item.PK && { PK: item.PK }),
            ...(item.SK && { SK: item.SK }),
            ...(item.GSI1PK && { GSI1PK: item.GSI1PK }),
            ...(item.GSI1SK && { GSI1SK: item.GSI1SK }),
            ...(item.GSI2PK && { GSI2PK: item.GSI2PK }),
            ...(item.GSI2SK && { GSI2SK: item.GSI2SK }),
            ...(item.createdBy && { createdBy: item.createdBy }),
          }
        }));
        setApiKeys(mappedKeys);
      } catch (err) {
        console.warn('Error loading app data:', err);
        setEnvironmentVariables([]);
        setApiKeys([]);
      }
    };

    loadAppData();
  }, [id, selectedAppId]);

  // Permitir portal de configuración cuando el mount esté listo (ref asignado)
  useLayoutEffect(() => {
    if (activeTab === 'apps' && activeAppTab === 'configuration' && selectedAppId && configMountRef.current) {
      setConfigMountReady(true);
    } else {
      setConfigMountReady(false);
    }
  }, [activeTab, activeAppTab, selectedAppId]);

  // En apps estáticas no existe el sub-tab de conectividad; evitar panel vacío
  useEffect(() => {
    if (service?.metadata?.appType === 'static' && configSubTab === 'connectivity') {
      setConfigSubTab('envVars');
    }
  }, [service?.metadata?.appType, configSubTab]);

  // Cargar GitHub config de la app seleccionada
  useEffect(() => {
    if (!selectedAppId) {
      setGithubConfig(null);
      setGithubConnected(false);
      setDeployStatus({ status: 'no_deploys' });
      return;
    }

    let cancelled = false;

    const loadGithubConfig = async () => {
      try {
        setIsLoadingGithubConfig(true);
        setGithubConnected(false);
        setGithubConfig(null);
        setSelectedRepo(null);
        setSelectedBranch('main');
        setDeployStatus({ status: 'no_deploys' });
        const config = await servicesService.getGithubConfig(selectedAppId);
        if (cancelled) return;
        setGithubConfig(config);

        if (config?.connected) {
          setGithubConnected(true);
          setSelectedRepo({
            full_name: config.repoFullName,
            name: config.repoFullName?.split('/')[1] || '',
          });
          setSelectedBranch(config.branch || 'main');
          // Determinar startMode default segun tecnologia del servicio
          const defaultStartMode = (() => {
            const tech = (service?.metadata?.technology || '').toLowerCase();
            if (tech.includes('python') || tech.includes('django') || tech.includes('flask') || tech.includes('fastapi')) {
              return 'gunicorn';
            }
            if (tech.includes('dotnet') || tech.includes('netcore') || tech.includes('.net')) {
              return 'dotnet';
            }
            return 'npm';
          })();
          setGithubStartMode(config.startMode || defaultStartMode);
          setGithubTargetMode(config.targetMode || getDefaultTargetModeForTechnology());
        } else {
          setGithubConnected(false);
          setSelectedRepo(null);
          setSelectedBranch('main');
          setGithubStartMode(getDefaultStartModeForTechnology());
          setGithubTargetMode(getDefaultTargetModeForTechnology());
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('Error loading GitHub config for app:', err);
        setGithubConfig(null);
        setGithubConnected(false);
      } finally {
        if (!cancelled) {
          setIsLoadingGithubConfig(false);
        }
      }
    };

    loadGithubConfig();

    return () => { cancelled = true; };
  }, [selectedAppId]);

  useEffect(() => {
    if (!selectedAppId) {
      setDeployHistory([]);
      setDeployHistoryNextToken(undefined);
      setHasMoreDeploys(false);
      return;
    }

    const loadDeployHistory = async () => {
      try {
        setIsLoadingDeployHistory(true);
        const response = await servicesService.getDeployHistory(selectedAppId, 10);
        setDeployHistory(response.deploys);
        setDeployHistoryNextToken(response.nextToken);
        setHasMoreDeploys(!!response.nextToken);
      } catch (err) {
        console.warn('Error loading deploy history:', err);
        setDeployHistory([]);
      } finally {
        setIsLoadingDeployHistory(false);
      }
    };

    loadDeployHistory();
  }, [selectedAppId]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ghConnected = urlParams.get('github_connected');
    const returnAppId = urlParams.get('app_id');

    if (ghConnected === 'true') {
      window.history.replaceState({}, '', window.location.pathname);

      const targetAppId = returnAppId || selectedAppId;
      if (!targetAppId) return;

      const loadRepos = async () => {
        try {
          setIsConnectingGithub(true);
          const repos = await servicesService.getGithubRepos(targetAppId);
          setGithubRepos(repos);
          setGithubConnected(true);
          setShowGithubModal(true);
        } catch (err: any) {
          showError(err.message || 'Error al cargar los repositorios');
          setGithubConnected(false);
        } finally {
          setIsConnectingGithub(false);
        }
      };

      loadRepos();
    }
  }, [selectedAppId, showError]);

  useEffect(() => {
    if (!selectedAppId || !githubConfig) return;

    const deployInfo = githubConfig.lastDeploy || githubConfig.initialDeploy;
    if (!deployInfo) return;

    const deployStatusValue = deployInfo.status;

    if (deployStatusValue === 'pending' || deployStatusValue === 'in_progress') {
      checkDeployStatus();

      const interval = setInterval(() => {
        checkDeployStatus();
      }, 5000);

      return () => clearInterval(interval);
    } else {
      checkDeployStatus();
    }
  }, [selectedAppId, githubConfig?.lastDeploy?.status, githubConfig?.lastDeploy?.commandId,
    githubConfig?.initialDeploy?.status, githubConfig?.initialDeploy?.commandId]);


  const checkDeployStatus = async () => {
    if (!selectedAppId) return;

    try {
      setIsCheckingDeployStatus(true);
      const status = await servicesService.getGithubDeployStatus(selectedAppId);
      setDeployStatus(status);
      setLastCheckedAt(new Date());

      if (status.lastDeploy && (status.status === 'success' || status.status === 'failed')) {
        setGithubConfig((prev: any) => prev ? {
          ...prev,
          lastDeploy: status.lastDeploy
        } : null);
      }
    } catch (err: any) {
      console.error('Error checking deploy status:', err);
    } finally {
      setIsCheckingDeployStatus(false);
    }
  };

  const handleRefresh = async () => {
    if (!id) return;

    try {
      setIsRefreshingEvents(true);
      const serviceData: any = await servicesService.getById(id);

      if (isAppDeployServiceType(serviceData.type)) {
        const normalizedStatus = serviceData.status?.toLowerCase() || serviceData.status;

        // Convertir fechas a Date si son strings
        const createdAt = serviceData.createdAt
          ? (typeof serviceData.createdAt === 'string' ? new Date(serviceData.createdAt) : serviceData.createdAt)
          : new Date();
        const updatedAt = serviceData.updatedAt
          ? (typeof serviceData.updatedAt === 'string' ? new Date(serviceData.updatedAt) : serviceData.updatedAt)
          : new Date();

        const mappedService: AppDeployService = {
          ...serviceData,
          type: 'APP_DEPLOY',
          status: normalizedStatus as any,
          publicUrl: serviceData.publicUrl,
          metadata: serviceData.metadata || {},
          createdAt,
          updatedAt,
        };

        setService(mappedService);
      }

      const eventsData = await servicesService.getEvents(id);
      const mappedEvents = mapServiceEventsToUI(eventsData);
      setEvents(mappedEvents);

      success(t('deployApp.detail.serviceUpdated'));
    } catch (err: any) {
      showError(err.message || 'Error al actualizar el servicio');
    } finally {
      setIsRefreshingEvents(false);
    }
  };

  const handleStaticZipDeploy = async () => {
    if (!zipFile || !id || !service || !selectedAppId) return;

    setUploadPhase('requesting');
    setUploadProgress(0);
    setUploadError('');
    setIsUploadingZip(true);
    setRetryUploadAfterDelete(false);

    try {
      // Paso 1: Solicitar presigned URL + crear deploy record
      setUploadPhase('requesting');
      const technology = service.metadata?.technology || service.metadata?.appConfig?.type || '';
      const uploadInfo = await servicesService.requestStaticDeployUpload(
        selectedAppId,
        id,
        `Deploy ZIP: ${zipFile.name}`,
        'production',
        technology
      );

      // Paso 2: Subir ZIP directo a S3 (trigger automático S3 → SQS → worker)
      setUploadPhase('uploading');
      await servicesService.uploadZipToS3(
        uploadInfo.uploadUrl,
        zipFile,
        (percent) => setUploadProgress(percent)
      );

      // Upload completo - el worker SQS procesará automáticamente
      setUploadPhase('done');
      setUploadProgress(100);
      setZipFile(null);
      success(t('deployApp.detail.deployUploaded'));

      // Recargar historial de deploys
      if (selectedAppId) {
        try {
          const response = await servicesService.getDeployHistory(selectedAppId, 10);
          setDeployHistory(response.deploys);
          setDeployHistoryNextToken(response.nextToken);
          setHasMoreDeploys(!!response.nextToken);
        } catch (err) {
          console.warn('Error refreshing deploy history:', err);
        }
      }

      // Reset después de 5 segundos
      setTimeout(() => {
        setUploadPhase('idle');
        setUploadProgress(0);
      }, 5000);

    } catch (err: any) {
      // Handle 409 MAX_RELEASES_REACHED error
      if (err?.status === 409) {
        const errorCode = err?.code || (err?.errorData?.error?.code);
        if (errorCode === 'MAX_RELEASES_REACHED') {
          // Show modal for storage limit reached
          setStorageLimitData({
            currentReleases: err?.errorData?.error?.currentReleases,
            maxReleases: err?.errorData?.error?.maxReleases,
            oldestRelease: err?.errorData?.error?.oldestRelease,
          });
          setShowStorageLimitModal(true);
          setRetryUploadAfterDelete(true);
          setUploadPhase('idle');
          setIsUploadingZip(false);
          return;
        }
      }

      setUploadPhase('error');
      const errorMsg = err?.message || 'Error al desplegar el archivo';
      setUploadError(errorMsg);
      showError(errorMsg);
    } finally {
      if (!retryUploadAfterDelete) {
        setIsUploadingZip(false);
      }
    }
  };

  const handleStorageLimitModalDeleteSuccess = async () => {
    // After successful delete, retry the upload
    if (retryUploadAfterDelete && zipFile) {
      await handleStaticZipDeploy();
    }
  };

  const handleDelete = async () => {
    if (!id || !service) return;

    try {
      setIsDeleting(true);
      await servicesService.delete(id);
      success(t('deployApp.detail.appDeleted'));
      navigate('/deploy-apps');
    } catch (err: any) {
      showError(err.message || 'Error al eliminar la aplicación');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleChangePlan = async () => {
    if (!id || !selectedNewPlan) return;
    setIsChangingPlan(true);
    setChangePlanError(null);
    try {
      await servicesService.changePlan(id, selectedNewPlan);
      success('Cambio de plan iniciado. El servicio tendrá ~2-3 min de downtime.');
      setShowChangePlanModal(false);
      setSelectedNewPlan('');
      const updatedService: any = await servicesService.getById(id);
      if (updatedService) setService((prev) => prev ? { ...prev, ...updatedService } : prev);
    } catch (err: any) {
      setChangePlanError(err?.message || 'Error al cambiar el plan. Intenta de nuevo.');
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleGenerateApiKey = () => {
    setApiKeyName('');
    setApiKeyExpirationDays('');
    setApiKeyUnlimited(false);
    setShowApiKeyNameModal(true);
  };

  const handleConfirmGenerateApiKey = async () => {
    if (!id || !service) return;

    if (!apiKeyName.trim()) {
      showError(t('deployApp.detail.apiKeyNameRequired'));
      return;
    }

    // Usar la app seleccionada
    if (!selectedAppId) {
      showError(t('deployApp.detail.selectAppBeforeDeploy'));
      return;
    }

    const appId = selectedAppId;

    // Validar días de expiración si no es ilimitado
    if (!apiKeyUnlimited && apiKeyExpirationDays) {
      const days = parseInt(apiKeyExpirationDays);
      if (isNaN(days) || days < 1) {
        showError(t('deployApp.detail.expirationDaysInvalid'));
        return;
      }
    }

    try {
      setIsGeneratingApiKey(true);
      const expirationDays = apiKeyUnlimited ? 0 : (apiKeyExpirationDays ? parseInt(apiKeyExpirationDays) : undefined);
      const newApiKey = await servicesService.generateApiKey(appId, id, apiKeyName.trim(), expirationDays);

      // Guardar el token temporalmente para mostrarlo en el modal
      // NO guardamos el token en apiKeys porque no queremos almacenarlo
      setGeneratedToken(newApiKey.token || '');
      setGeneratedTokenInfo(newApiKey.tokenInfo || null);

      // Cerrar el modal de nombre y abrir el modal de token
      setShowApiKeyNameModal(false);
      setApiKeyName('');
      setApiKeyExpirationDays('');
      setApiKeyUnlimited(false);
      setShowTokenModal(true);

      // Recargar las API keys desde el servidor (sin el token completo)
      try {
        const response: any = await servicesService.getApiKeys(appId);
        let items: any[] = [];
        if (Array.isArray(response)) {
          items = response;
        } else if (response && typeof response === 'object' && 'items' in response && Array.isArray((response as any).items)) {
          items = (response as any).items;
        } else if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as any).data)) {
          items = (response as any).data;
        } else if (response && typeof response === 'object' && 'tokens' in response && Array.isArray((response as any).tokens)) {
          items = (response as any).tokens;
        }

        const mappedKeys = items.map((item: any) => ({
          token: '', // No guardamos el token completo
          tokenInfo: {
            tenantId: item.tenantId,
            serviceId: item.serviceId,
            appId: item.appId,
            tokenId: item.tokenId,
            name: item.name,
            scope: item.scope,
            status: item.status || 'active',
            createdAt: item.createdAt,
            lastUsedAt: item.lastUsedAt,
            expiresAt: item.expiresAt,
            ...(item.PK && { PK: item.PK }),
            ...(item.SK && { SK: item.SK }),
            ...(item.GSI1PK && { GSI1PK: item.GSI1PK }),
            ...(item.GSI1SK && { GSI1SK: item.GSI1SK }),
            ...(item.GSI2PK && { GSI2PK: item.GSI2PK }),
            ...(item.GSI2SK && { GSI2SK: item.GSI2SK }),
            ...(item.createdBy && { createdBy: item.createdBy }),
          }
        }));

        setApiKeys(mappedKeys);
      } catch (err) {
        console.warn('Error reloading API keys:', err);
      }
    } catch (err: any) {
      showError(err.message || 'Error al generar la API key');
    } finally {
      setIsGeneratingApiKey(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!id || !apiKeyToRevoke) return;

    try {
      setIsRevokingApiKey(apiKeyToRevoke);
      await servicesService.revokeApiKey(id, apiKeyToRevoke);
      setApiKeys(prev => Array.isArray(prev) ? prev.map(key => {
        const tokenInfo = key.tokenInfo || {};
        if (tokenInfo.tokenId === apiKeyToRevoke) {
          return {
            ...key,
            tokenInfo: {
              ...tokenInfo,
              status: 'revoked' as const
            }
          };
        }
        return key;
      }) : []);
      success(t('deployApp.detail.tokenRevoked'));
      setShowRevokeModal(false);
      setApiKeyToRevoke(null);
    } catch (err: any) {
      showError(err.message || 'Error al revocar la API key');
    } finally {
      setIsRevokingApiKey(null);
    }
  };

  // ============================================================
  // Custom Domains
  //
  // El backend expone los endpoints:
  //   GET  /services/{id}/custom-domain/{appId}  → lista dominios de la app
  //   POST /services/{id}/register-custom-domain → body { appId, domainName }
  //   POST /services/{id}/validate-dns           → body { domainId }
  //
  // Como el GET ya es por app, mantenemos `customDomains` con el listado de
  // la app actualmente seleccionada y lo recargamos al cambiar de app.
  // ============================================================
  const loadCustomDomains = async (silent = false) => {
    if (!id || !selectedAppId) return;
    try {
      if (!silent) setIsLoadingDomains(true);
      const list = await servicesService.getCustomDomains(id, selectedAppId);
      setCustomDomains(Array.isArray(list) ? list : []);
      setDomainsLoaded(true);
    } catch (err: any) {
      console.error('Error loading custom domains:', err);
      if (!silent) {
        showError(err?.message || t('deployApp.detail.errorLoadingDomains'));
      }
    } finally {
      if (!silent) setIsLoadingDomains(false);
    }
  };

  // Resetear estado cuando cambia la app seleccionada
  // (el GET es por app: la lista no se reusa entre apps)
  useEffect(() => {
    setCustomDomains([]);
    setDomainsLoaded(false);
    setNewDomainInput('');
    setDomainInputError(null);
  }, [selectedAppId]);

  // Cargar los dominios la primera vez que se abre el sub-tab "domains" para la app seleccionada
  useEffect(() => {
    if (activeTab !== 'apps') return;
    if (activeAppTab !== 'configuration') return;
    if (configSubTab !== 'domains') return;
    if (!id) return;
    if (!selectedAppId) return;
    if (domainsLoaded) return;
    loadCustomDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeAppTab, configSubTab, id, selectedAppId, domainsLoaded]);

  /**
   * Lista de dominios visibles en la card de la app actual.
   * El GET ya filtra por appId, así que esto es directo. Como segundo nivel
   * de seguridad, si el backend devolviera items con appId distinto al
   * seleccionado, los excluimos.
   */
  const visibleCustomDomains = customDomains.filter((d) => !d.appId || d.appId === selectedAppId);

  const handleDomainInputChange = (value: string) => {
    setNewDomainInput(value);
    if (domainInputError) {
      // Re-validar para limpiar el error en cuanto el usuario corrige
      const existing = visibleCustomDomains.map((d) => d.domainName);
      const err = validateCustomDomain(value, existing);
      setDomainInputError(err);
    }
  };

  const handleAddCustomDomain = async () => {
    if (!id || !selectedAppId) return;

    if (service?.metadata?.appType === 'static' && visibleCustomDomains.length >= 1) {
      setDomainInputError('staticDomainLimit');
      return;
    }

    const existing = visibleCustomDomains.map((d) => d.domainName);
    const err = validateCustomDomain(newDomainInput, existing);
    if (err) {
      setDomainInputError(err);
      return;
    }

    const normalized = normalizeDomain(newDomainInput);
    try {
      setIsAddingDomain(true);
      setDomainInputError(null);
      const created = await servicesService.registerCustomDomain(id, {
        appId: selectedAppId,
        domainName: normalized,
      });

      // Asegurar que el item creado tenga appId asociado en el cliente
      // (por si el backend no lo incluye en la respuesta del registro).
      const createdWithAppId: CustomDomain = created.appId
        ? created
        : { ...created, appId: selectedAppId };

      setCustomDomains((prev) => [...prev, createdWithAppId]);
      setNewDomainInput('');
      success(
        created.message ||
          (service?.metadata?.appType === 'static'
            ? t('deployApp.detail.domainAddedStatic')
            : t('deployApp.detail.domainAdded'))
      );
    } catch (e: any) {
      const apiMsg = e?.message || t('deployApp.detail.errorAddingDomain');
      // Si el backend devuelve un error específico para duplicados, lo reflejamos en el input
      if (/exists|duplicate|already/i.test(apiMsg)) {
        setDomainInputError('domainDuplicate');
      } else {
        showError(apiMsg);
      }
    } finally {
      setIsAddingDomain(false);
    }
  };

  const handleVerifyCustomDomain = async (domain: CustomDomain) => {
    if (!id || !selectedAppId) return;
    try {
      setVerifyingDomainId(domain.domainId);
      const updated = await servicesService.verifyCustomDomain(id, selectedAppId, domain.domainId, domain);

      if (!updated) {
        // El dominio ya no existe; lo quitamos de la lista local
        setCustomDomains((prev) => prev.filter((d) => d.domainId !== domain.domainId));
        return;
      }

      setCustomDomains((prev) =>
        prev.map((d) =>
          d.domainId === domain.domainId ? mergeCustomDomain(d, updated) : d
        )
      );

      const updatedStatus = (updated.status || '').toUpperCase();
      const updatedCertStatus = (updated.certificateStatus || '').toUpperCase();
      const isStaticFlow = service?.metadata?.appType === 'static';

      if (updatedStatus === 'ACTIVE') {
        success(t('deployApp.detail.domainVerifiedToast'));
      } else if (isStaticFlow && updatedCertStatus === 'ISSUED') {
        success(updated.message || t('deployApp.detail.certValidatedToast'));
      } else if (isStaticFlow && updatedCertStatus !== 'ISSUED') {
        showError(updated.message || t('deployApp.detail.certStillPending'));
      } else {
        showError(updated.message || t('deployApp.detail.domainStillPending'));
      }
    } catch (e: any) {
      showError(e?.message || t('deployApp.detail.errorVerifyingDomain'));
    } finally {
      setVerifyingDomainId(null);
    }
  };

  const openDomainsConfiguration = () => {
    setActiveTab('apps');
    setActiveAppTab('configuration');
    setConfigSubTab('domains');
  };

  const handleRequestDeleteDomain = (domain: CustomDomain) => {
    setDomainToDelete(domain);
    setShowDeleteDomainModal(true);
  };

  const handleConfirmDeleteDomain = async () => {
    if (!id || !domainToDelete) return;
    try {
      setIsDeletingDomain(true);
      await servicesService.deleteCustomDomain(id, domainToDelete.domainId);
      setCustomDomains((prev) => prev.filter((d) => d.domainId !== domainToDelete.domainId));
      success(t('deployApp.detail.domainDeleted'));
      setShowDeleteDomainModal(false);
      setDomainToDelete(null);
    } catch (e: any) {
      showError(e?.message || t('deployApp.detail.errorDeletingDomain'));
    } finally {
      setIsDeletingDomain(false);
    }
  };

  const handleCopyDomainField = (text: string, fieldId: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => {
      setCopiedField((current) => (current === fieldId ? null : current));
    }, 1800);
  };

  const maskApiKey = (token: string | undefined, tokenId?: string): string => {
    // Si hay token completo, enmascararlo
    if (token && token.length > 0) {
      if (token.length <= 8) return '••••••••';
      // Mostrar primeros 4 y últimos 4 caracteres
      return token.substring(0, 4) + '••••••••' + token.substring(token.length - 4);
    }
    // Si solo tenemos tokenId, mostrar tokenId enmascarado
    if (tokenId) {
      if (tokenId.length <= 8) return '••••••••';
      return tokenId.substring(0, 4) + '••••••••' + tokenId.substring(tokenId.length - 4);
    }
    return '••••••••';
  };

  const copyApiKey = (token: string | undefined, tokenId?: string) => {
    // Si hay token completo, copiarlo
    if (token && token.length > 0) {
      navigator.clipboard.writeText(token);
      success(t('deployApp.detail.tokenCopied'));
    } else if (tokenId) {
      // Si solo tenemos tokenId, copiar el tokenId
      navigator.clipboard.writeText(tokenId);
      success(t('deployApp.detail.tokenIdCopied'));
    } else {
      showError(t('deployApp.detail.noTokenToCopy'));
    }
  };

  const formatExpirationDate = (expiresAt: number | null | undefined): string | null => {
    if (!expiresAt) return null;
    // expiresAt es un timestamp numérico (segundos desde epoch)
    const date = new Date(expiresAt * 1000); // Convertir a milisegundos
    return date.toLocaleString();
  };

  // Funciones para manejar variables de entorno
  const handleAddEnvVar = () => {
    setEnvironmentVariables([...environmentVariables, { name: '', value: '', hidden: false }]);
    setIsEditingEnvVars(true);
  };

  const handleUpdateEnvVar = (index: number, field: 'name' | 'value' | 'hidden', value: string | boolean) => {
    const newVars = [...environmentVariables];
    if (field === 'hidden') {
      newVars[index][field] = value as boolean;
    } else {
      newVars[index][field] = value as string;
    }
    setEnvironmentVariables(newVars);
    setIsEditingEnvVars(true);
  };

  const handleRemoveEnvVar = (index: number) => {
    const newVars = environmentVariables.filter((_, i) => i !== index);
    setEnvironmentVariables(newVars);
    setIsEditingEnvVars(true);
  };

  const handleSaveEnvVars = async () => {
    if (!id || !service) return;

    // Validar que todas las variables tengan nombre
    const invalidVars = environmentVariables.filter(v => !v.name.trim());
    if (invalidVars.length > 0) {
      showError(t('deployApp.detail.envVarNameRequired'));
      return;
    }

    // Usar la app seleccionada
    if (!selectedAppId) {
      showError(t('deployApp.detail.selectAppBeforeDeploy'));
      return;
    }

    const appId = selectedAppId;

    try {
      setIsSavingEnvVars(true);

      // Filtrar variables válidas (con nombre y valor)
      const validVars = environmentVariables
        .filter(v => v.name.trim() && v.value.trim())
        .map(v => ({
          name: v.name.trim(),
          value: v.value.trim(),
          hidden: v.hidden || false
        }));

      // Hacer PATCH a /apps/{appId}/environment-vars con el payload { vars: [...] }
      await servicesService.updateEnvironmentVars(appId, validVars);

      // Recargar la información de la app para obtener las variables actualizadas
      try {
        const appInfo = await servicesService.getAppById(appId);
        if (appInfo.envVars && Array.isArray(appInfo.envVars)) {
          setEnvironmentVariables([...appInfo.envVars]);
        } else {
          setEnvironmentVariables(validVars);
        }
      } catch (err) {
        console.warn('Error reloading app info after save:', err);
        // Si falla, usar las variables que acabamos de guardar
        setEnvironmentVariables(validVars);
      }

      success(t('deployApp.detail.envVarsSaved'));
      setIsEditingEnvVars(false);
    } catch (err: any) {
      showError(err.message || 'Error al guardar las variables de entorno');
    } finally {
      setIsSavingEnvVars(false);
    }
  };

  const handleCancelEditEnvVars = async () => {
    // Restaurar las variables de entorno originales desde el API de apps
    if (!selectedAppId) {
      setIsEditingEnvVars(false);
      return;
    }

    try {
      const appId = selectedAppId;
      const appInfo = await servicesService.getAppById(appId);
      // Cargar variables de entorno desde envVars del API de apps
      if (appInfo.envVars && Array.isArray(appInfo.envVars)) {
        setEnvironmentVariables([...appInfo.envVars]);
      } else {
        setEnvironmentVariables([]);
      }
    } catch (err) {
      console.warn('Error loading app info on cancel:', err);
    }
    setIsEditingEnvVars(false);
  };

  const handleCreateApp = async () => {
    if (!id || !service) return;

    // Validar campos requeridos
    if (!newAppData.name.trim()) {
      showError(t('deployApp.detail.appNameRequired'));
      return;
    }
    if (!newAppData.environment.trim()) {
      showError(t('deployApp.detail.environmentRequired'));
      return;
    }
    if (!newAppData.startMode.trim()) {
      showError(t('deployApp.detail.startModeRequired'));
      return;
    }
    // Para .NET con startMode=auto/publish el target es opcional (lo autodetecta el script).
    {
      const tech = (service?.metadata?.technology || service?.metadata?.appConfig?.type || '').toString().toLowerCase();
      const isNetcore = tech.includes('netcore') || tech.includes('dotnet') || tech.includes('.net');
      const isAutoOrPublish = newAppData.startMode.trim() === 'auto' || newAppData.startMode.trim() === 'publish';
      const isTargetOptional = isNetcore && isAutoOrPublish;
      if (!isTargetOptional && !newAppData.targetMode.trim()) {
        showError(t('deployApp.detail.targetFileRequired'));
        return;
      }
    }
    const serviceSubdomain = (service.metadata?.appConfig?.subdomain ?? '').trim();
    if (!serviceSubdomain) {
      showError(t('deployApp.detail.subdomainRequiredAtService') || 'Este servicio no tiene subdominio configurado. El subdominio se define al crear el servicio.');
      return;
    }
    const slugTrim = (newAppData.slug ?? '').trim();
    if (!slugTrim) {
      showError(t('deployApp.detail.appSlugRequired') || 'El slug o dominio de la app es requerido.');
      return;
    }
    const existingSlugsOrNames = (service?.apps || []).map((a: any) => [(a.name || '').trim().toLowerCase(), (a.slug || '').trim().toLowerCase()].filter(Boolean)).flat();
    if (existingSlugsOrNames.includes(slugTrim.toLowerCase())) {
      showError(t('deployApp.detail.appSlugDuplicate') || 'Ese slug ya está en uso por otra app de este servicio. Elige uno distinto.');
      return;
    }

    const appConfig = service.metadata?.appConfig || {};
    const technology = service.metadata?.technology || appConfig.type || '';
    const deploymentType = appConfig.deploymentType || '';
    const runtime = appConfig.runtime;

    try {
      setIsCreatingApp(true);

      // Solo appConfig, como en el alta de servicio (orderData.metadata.appConfig)
      const appPayload = {
        name: newAppData.name.trim(),
        description: (newAppData.description || '').trim() || undefined,
        subdomain: serviceSubdomain || undefined,
        slug: slugTrim || undefined,
        type: technology || undefined,
        environment: newAppData.environment.trim(),
        autoDeploy: appConfig.autoDeploy !== undefined ? appConfig.autoDeploy : true,
        premiumLogs: appConfig.premiumLogs || false,
        premiumLogsPlan: appConfig.premiumLogsPlan ?? undefined,
        environmentVariables: appConfig.environmentVariables || [],
        deploymentType: deploymentType || undefined,
        runtime: runtime ? { technology: runtime.technology || technology, version: runtime.version || '' } : undefined,
        startMode: newAppData.startMode.trim(),
        targetMode: newAppData.targetMode.trim(),
      };

      const createdApp = await servicesService.createApp(id, appPayload);

      success(t('deployApp.detail.appCreated'));

      setShowAddAppModal(false);
      setNewAppData({
        name: '',
        description: '',
        slug: '',
        environment: '',
        startMode: '',
        targetMode: '',
      });

      try {
        const serviceData: any = await servicesService.getById(id);
        if (serviceData && isAppDeployServiceType(serviceData.type)) {
          const createdAt = serviceData.createdAt
            ? (typeof serviceData.createdAt === 'string' ? new Date(serviceData.createdAt) : serviceData.createdAt)
            : new Date();
          const updatedAt = serviceData.updatedAt
            ? (typeof serviceData.updatedAt === 'string' ? new Date(serviceData.updatedAt) : serviceData.updatedAt)
            : new Date();

          const mappedService: AppDeployService = {
            ...serviceData,
            type: 'APP_DEPLOY',
            status: (serviceData.status?.toLowerCase() || serviceData.status) as any,
            publicUrl: serviceData.publicUrl,
            metadata: serviceData.metadata || {},
            createdAt,
            updatedAt,
          };

          setService(mappedService);

          if (createdApp?.id && serviceData.apps) {
            const newApp = serviceData.apps.find((a: any) => a.id === createdApp.id);
            if (newApp) {
              setSelectedAppId(newApp.id);
              setActiveAppTab('overview');
            } else if (serviceData.apps.length > 0) {
              setSelectedAppId(serviceData.apps[0].id);
            }
          } else if (serviceData.apps && serviceData.apps.length > 0) {
            setSelectedAppId(serviceData.apps[0].id);
          }
        }
      } catch (err) {
        console.warn('Error reloading service after app creation:', err);
      }
    } catch (err: any) {
      console.error('Error creating app:', err);
      showError(err.response?.data?.message || err.message || 'Error al crear la app. Por favor, intenta de nuevo.');
    } finally {
      setIsCreatingApp(false);
    }
  };

  const getDefaultStartModeForTechnology = (): string => {
    const tech = (service?.metadata?.technology || '').toLowerCase();
    if (tech.includes('python') || tech.includes('django') || tech.includes('flask') || tech.includes('fastapi')) {
      return 'gunicorn';
    }
    if (tech === 'go' || tech === 'golang') {
      return 'go';
    }
    if (tech.includes('dotnet') || tech.includes('netcore') || tech.includes('.net')) {
      return 'auto';
    }
    return 'npm';
  };

  const getDefaultTargetModeForTechnology = (): string => {
    const tech = (service?.metadata?.technology || '').toLowerCase();
    if (tech.includes('python') || tech.includes('django') || tech.includes('flask') || tech.includes('fastapi')) {
      return 'app:app';
    }
    if (tech === 'go' || tech === 'golang') {
      return 'main.go';
    }
    if (tech.includes('dotnet') || tech.includes('netcore') || tech.includes('.net')) {
      return '';
    }
    return 'start';
  };

  const handleConnectGithub = () => {
    if (!selectedAppId) {
      showError(t('deployApp.detail.selectAppBeforeDeploy'));
      return;
    }

    setIsConnectingGithub(true);

    const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID || 'TU_GITHUB_CLIENT_ID';
    const redirectUri = `${window.location.origin}/github-callback`;
    const state = btoa(JSON.stringify({
      serviceId: id,
      appId: selectedAppId,
      returnUrl: window.location.pathname,
      tenantId: user?.tenantId || ''
    }));

    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=repo,admin:repo_hook&state=${state}`;
  };

  const handleConfigureGithubDeploy = async () => {
    if (!selectedAppId || !selectedRepo || !selectedBranch) return;

    try {
      setIsConfiguringDeploy(true);

      await servicesService.configureGithubDeploy(selectedAppId, {
        repoId: selectedRepo.id,
        repoFullName: selectedRepo.full_name,
        branch: selectedBranch,
        startMode: githubStartMode,
        targetMode: githubTargetMode,
      });

      setGithubConfig({
        connected: true,
        repoFullName: selectedRepo.full_name,
        branch: selectedBranch,
        startMode: githubStartMode,
        targetMode: githubTargetMode,
      });

      success(t('deployApp.detail.autoDeployConfigured'));
      setShowGithubModal(false);
      setGithubConnected(true);
    } catch (err: any) {
      showError(err.message || 'Error al configurar el deploy automático');
    } finally {
      setIsConfiguringDeploy(false);
    }
  };

  const handleDisconnectGithub = async () => {
    if (!selectedAppId) return;

    try {
      await servicesService.disconnectGithub(selectedAppId);

      setGithubConnected(false);
      setGithubConfig(null);
      setSelectedRepo(null);
      setSelectedBranch('main');

      success(t('deployApp.detail.githubDisconnected'));
    } catch (err: any) {
      showError(err.message || 'Error al desconectar GitHub');
    }
  };

  const handleLoadMoreDeploys = async () => {
    if (!selectedAppId || !deployHistoryNextToken) return;
    try {
      setIsLoadingDeployHistory(true);
      const response = await servicesService.getDeployHistory(selectedAppId, 10, deployHistoryNextToken);
      setDeployHistory(prev => [...prev, ...response.deploys]);
      setDeployHistoryNextToken(response.nextToken);
      setHasMoreDeploys(!!response.nextToken);
    } catch (err) {
      console.warn('Error loading more deploys:', err);
    } finally {
      setIsLoadingDeployHistory(false);
    }
  };

  const handleRollback = async (deploy: DeployRecord) => {
    if (!id || !selectedAppId) return;
    setRollingBackDeployId(deploy.deployId);
    try {
      await servicesService.rollbackAppDeploy(selectedAppId, {
        serviceId: id,
        appId: deploy.appId,
        deployId: deploy.deployId,
      });
      success(t('deployApp.detail.rollbackSuccess'));
      const response = await servicesService.getDeployHistory(selectedAppId, 10);
      setDeployHistory(response.deploys);
      setDeployHistoryNextToken(response.nextToken);
      setHasMoreDeploys(!!response.nextToken);
    } catch (err: any) {
      showError(err?.response?.data?.message || err?.message || t('deployApp.detail.rollbackError'));
    } finally {
      setRollingBackDeployId(null);
    }
  };

  const getDeployStatusBadge = (status: string) => {
    const s = status.toUpperCase();
    switch (s) {
      case 'SUCCESS':
      case 'COMPLETED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300">
            <CheckCircleOutlined className="text-[10px]" /> {t('deployApp.detail.statusSuccess')}
          </span>
        );
      case 'FAILED':
      case 'ERROR':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300">
            <CloseCircleOutlined className="text-[10px]" /> {t('deployApp.detail.statusFailed')}
          </span>
        );
      case 'DEPLOYING':
      case 'IN_PROGRESS':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
            <LoadingOutlined spin className="text-[10px]" /> {t('deployApp.detail.statusInProgress')}
          </span>
        );
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300">
            <ClockCircleOutlined className="text-[10px]" /> {t('deployApp.detail.statusPending')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
            {status}
          </span>
        );
    }
  };

  const handleFetchLogs = async (type?: 'deploy' | 'app' | 'app-error') => {
    if (!selectedAppId) return;
    const fetchType = type || logType;
    try {
      setIsLoadingLogs(true);
      setAppLogsError('');
      const response = await servicesService.getAppLogs(selectedAppId, fetchType, logLines);
      if (response.success) {
        setAppLogs(response.output || '(empty)');
      } else {
        setAppLogs(response.output || '');
        setAppLogsError(response.error || 'Error fetching logs');
      }
    } catch (err: any) {
      setAppLogsError(err.message || 'Error fetching logs');
      setAppLogs('');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingOutlined className="text-4xl text-blue-600" spin />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1600px] mx-auto px-8 py-6">
        <button
          onClick={() => navigate('/deploy-apps')}
          className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftOutlined />
          {t('deployApp.detail.backToDeployApps')}
        </button>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-800 dark:text-red-200">{error || t('deployApp.detail.serviceNotFound')}</p>
        </div>
      </div>
    );
  }

  const appConfig = service.metadata?.appConfig || {};
  const technology = service.metadata?.technology || appConfig.type || 'Unknown';
  const appType = service.metadata?.appType || 'Unknown';
  const deploymentType = appConfig.deploymentType || 'Unknown';
  const technologyImage = getTechnologyImage(technology);
  // La descripción puede estar en metadata.description o en appConfig.description
  const description = service.metadata?.description || appConfig.description || '';

  // Función para obtener las opciones de startMode según el tipo de tecnología
  const getStartModeOptions = (tech: string): Array<{ value: string; label: string }> => {
    if (!tech || tech === 'Unknown') return [];

    const techLower = tech.toLowerCase();

    // Node.js - incluye variantes como nodejs, node.js, nextjs, etc.
    if (techLower === 'nodejs' || techLower === 'node' || techLower === 'node.js' ||
      techLower.includes('node') || techLower.includes('nextjs') || techLower.includes('nuxt')) {
      return [
        { value: 'npm', label: 'npm' },
        { value: 'node', label: 'node' },
      ];
    }

    // Python - incluye variantes como python, python3, etc.
    if (techLower === 'python' || techLower === 'python3' || techLower.includes('python') ||
      techLower.includes('django') || techLower.includes('flask') || techLower.includes('fastapi')) {
      return [
        { value: 'python', label: 'python' },
        { value: 'python3', label: 'python3' },
        { value: 'uvicorn', label: 'uvicorn' },
        { value: 'gunicorn', label: 'gunicorn' },
        { value: 'flask', label: 'flask' },
        { value: 'django', label: 'django' },
      ];
    }

    // .NET Core - incluye variantes como netcore, dotnet, .net, etc.
    if (techLower === 'netcore' || techLower === 'dotnet' || techLower === '.net' ||
      techLower.includes('dotnet') || techLower.includes('netcore') || techLower.includes('asp.net')) {
      return [
        { value: 'auto', label: 'auto (recomendado)' },
        { value: 'publish', label: 'publish (forzar build)' },
        { value: 'dotnet', label: 'dotnet (run dll)' },
        { value: 'dotnet-run', label: 'dotnet run (dev)' },
        { value: 'bin', label: 'bin (self-contained)' },
      ];
    }

    // Go - incluye variantes como go, golang
    if (techLower === 'go' || techLower === 'golang' || techLower.includes('go') || techLower.includes('golang')) {
      return [
        { value: 'go', label: 'go' },
      ];
    }

    // Para otros tipos (java, php, etc.), retornar array vacío para usar input de texto
    return [];
  };

  const startModeOptions = getStartModeOptions(technology);
  const hasStartModeDropdown = startModeOptions.length > 0;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel={t('deployApp.detail.backToDeployApps')}
        onBack={() => navigate('/deploy-apps')}
        title={service.name}
        subtitle={`${technology} • ${appType === 'static' ? t('deployApp.detail.staticApp') : t('deployApp.detail.serverApp')} • ${deploymentType}`}
        icon={technologyImage ? (
          <img
            src={technologyImage}
            alt={technology}
            className="w-full h-full object-contain p-1"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : undefined}
        actions={
          !isServiceDeleted(service.status) && (
            <HeaderActionMenu
              items={[
                {
                  key: 'refresh',
                  label: t('deployApp.detail.refresh'),
                  icon: <ReloadOutlined className={isRefreshingEvents ? 'animate-spin' : ''} />,
                  onClick: () => handleRefresh(),
                  disabled: isRefreshingEvents,
                },
                {
                  key: 'delete',
                  label: 'Eliminar servicio',
                  icon: <DeleteOutlined />,
                  onClick: () => setShowDeleteModal(true),
                  danger: true,
                  disabled: isServiceDeleting(service.status),
                },
              ]}
            />
          )
        }
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-8 space-y-4 sm:space-y-6">
          {/* Identity Card — always visible across all tabs */}
          <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{service.name}</h2>
              <ServiceStatusBadge status={service.status} size="md" showLabel />
            </div>
            <div className="px-4 sm:px-6 py-4 space-y-3">
              {/* URL — primary action */}
              {service.publicUrl ? (
                <div className="flex items-center gap-2">
                  <GlobalOutlined className="text-gray-400 flex-shrink-0" />
                  <a
                    href={service.publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-mono truncate"
                  >
                    {service.publicUrl}
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
                  <GlobalOutlined className="flex-shrink-0" />
                  <span className="text-sm italic">URL no disponible aún</span>
                </div>
              )}

              {/* Specs inline — leídas directo del servicio */}
              {(() => {
                const instanceType = (service as any).instanceType as string | undefined;
                const volumeGB = (service as any).volumeGB as number | undefined;
                const ec2 = instanceType ? EC2_SPECS[instanceType] : null;
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    {instanceType && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-700">
                        <TagOutlined />
                        {instanceType}
                      </span>
                    )}
                    {ec2 && appType === 'server' && (
                      <>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                          <ThunderboltOutlined className="text-blue-400" />
                          {ec2.vcpu} vCPU
                        </span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                          <DatabaseOutlined className="text-purple-400" />
                          {formatMemory(ec2.memoryMB)} RAM
                        </span>
                        {volumeGB ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                            <HddOutlined className="text-green-400" />
                            {volumeGB} GB
                          </span>
                        ) : null}
                      </>
                    )}
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      appType === 'server'
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                        : 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                    }`}>
                      {appType === 'server' ? 'Servidor' : 'Estático'}
                    </span>
                  </div>
                );
              })()}

              {/* Service ID — for support/debugging */}
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">ID:</span>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded font-mono text-gray-600 dark:text-gray-300">
                  {service.id}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(service.id);
                    success(t('deployApp.detail.serviceIdCopied'));
                  }}
                  className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title={t('deployApp.detail.copyId')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex gap-1 px-4 sm:px-6 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex-shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview'
                    ? 'border-black dark:border-white text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  {t('deployApp.detail.overview')}
                </button>
                <button
                  onClick={() => setActiveTab('apps')}
                  className={`flex-shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'apps'
                    ? 'border-black dark:border-white text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  {appType === 'static' ? t('deployApp.detail.appSingular') : t('deployApp.detail.apps')}
                </button>
                {appType === 'server' && (
                  <button
                    onClick={() => setActiveTab('events')}
                    className={`flex-shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'events'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                  >
                    {t('deployApp.detail.events')}
                  </button>
                )}
                {appType === 'server' && (
                  <button
                    onClick={() => setActiveTab('monitoring')}
                    className={`flex-shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'monitoring'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                  >
                    Monitoring
                  </button>
                )}
                {appType === 'server' && (
                  <button
                    onClick={() => setActiveTab('backups')}
                    className={`flex-shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'backups'
                      ? 'border-black dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                  >
                    Respaldos
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {activeTab === 'overview' && (() => {
                const instanceType = (service as any).instanceType as string | undefined;
                const volumeGB = (service as any).volumeGB as number | undefined;
                const planSpecs = instanceType ? EC2_SPECS[instanceType] : null;

                const InfoRow = ({ icon, label, value, mono = false }: { icon: React.ReactNode; label: string; value: React.ReactNode; mono?: boolean }) => (
                  <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <span className="text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0">{icon}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 w-32 flex-shrink-0">{label}</span>
                    <span className={`text-sm font-medium text-gray-900 dark:text-white flex-1 min-w-0 break-words ${mono ? 'font-mono' : ''}`}>{value}</span>
                  </div>
                );

                return (
                  <div className="space-y-6">
                    {/* Recursos del servidor */}
                    {appType === 'server' && planSpecs && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">Recursos del servidor</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col items-center gap-1 text-center">
                            <ThunderboltOutlined className="text-blue-500 text-lg" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white">{planSpecs.vcpu}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">vCPU</span>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col items-center gap-1 text-center">
                            <DatabaseOutlined className="text-purple-500 text-lg" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white">{formatMemory(planSpecs.memoryMB)}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">RAM</span>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex flex-col items-center gap-1 text-center">
                            <HddOutlined className="text-green-500 text-lg" />
                            <span className="text-xl font-bold text-gray-900 dark:text-white">{volumeGB ?? '—'} GB</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Disco (EBS)</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                          Instancia: <span className="font-mono">{instanceType}</span> · AWS EC2 · {formatRegion(service.region)}
                        </p>
                      </div>
                    )}

                    {/* Plan e identidad */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Plan y servicio</h4>
                        {appType === 'server' && (
                          <button
                            type="button"
                            onClick={() => { setSelectedNewPlan(''); setChangePlanError(null); setShowChangePlanModal(true); }}
                            className="text-xs px-2 py-1 rounded text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            Cambiar plan
                          </button>
                        )}
                      </div>
                      <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {technology && technology !== 'Unknown' && (
                          <InfoRow
                            icon={<CodeOutlined />}
                            label="Tecnología"
                            value={
                              <span className="flex items-center gap-2">
                                {technologyImage && (
                                  <img
                                    src={technologyImage}
                                    alt={technology}
                                    className="w-4 h-4 object-contain"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                )}
                                {formatTechnology(technology)}
                              </span>
                            }
                          />
                        )}
                        {service.region && (
                          <InfoRow
                            icon={<EnvironmentOutlined />}
                            label="Región"
                            value={formatRegion(service.region)}
                          />
                        )}
                        <InfoRow
                          icon={<CalendarOutlined />}
                          label="Creado"
                          value={
                            <span>
                              {service.createdAt instanceof Date
                                ? service.createdAt.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                                : new Date(service.createdAt).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                              <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                                ({getServiceAge(service.createdAt)})
                              </span>
                            </span>
                          }
                        />
                      </div>
                    </div>

                    {/* Subscripción */}
                    {(() => {
                      const sub = (service as any).subscription as {
                        id?: string; status?: string; billingInterval?: string;
                        amount?: number; currency?: string; currentPeriodEnd?: string;
                        cancelAtPeriodEnd?: boolean;
                      } | null;
                      if (!sub) return null;

                      const statusConfig: Record<string, { label: string; className: string }> = {
                        ACTIVE:    { label: 'Activa',          className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
                        PAST_DUE:  { label: 'Pago pendiente',  className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
                        CANCELLED: { label: 'Cancelada',       className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                        SUSPENDED: { label: 'Suspendida',      className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
                      };
                      const sc = statusConfig[sub.status || ''] ?? { label: sub.status || '—', className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' };

                      const intervalLabel = sub.billingInterval === 'YEAR' ? 'anual' : 'mensual';
                      const amountFmt = sub.amount != null
                        ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: (sub.currency || 'usd').toUpperCase() }).format(sub.amount / 100)
                        : null;
                      const nextCharge = sub.currentPeriodEnd
                        ? new Date(sub.currentPeriodEnd).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                        : null;

                      return (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">Subscripción</h4>
                          <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            <InfoRow
                              icon={<TagOutlined />}
                              label="Estado"
                              value={<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.className}`}>{sc.label}</span>}
                            />
                            {amountFmt && (
                              <InfoRow
                                icon={<CalendarOutlined />}
                                label="Cobro"
                                value={`${amountFmt} ${intervalLabel}`}
                              />
                            )}
                            {nextCharge && (
                              <InfoRow
                                icon={<CalendarOutlined />}
                                label={sub.cancelAtPeriodEnd ? 'Cancela el' : 'Próximo cobro'}
                                value={nextCharge}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Configuración */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">Configuración</h4>
                      <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {appConfig.subdomain && (
                          <InfoRow
                            icon={<GlobalOutlined />}
                            label="Subdominio"
                            value={
                              <span className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-x-2 min-w-0">
                                <span className="font-mono break-all">{appConfig.subdomain}</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500 min-w-0">
                                  → <span className="font-mono break-all">*.{appConfig.subdomain}.apps1.kubiy.site</span>
                                </span>
                              </span>
                            }
                          />
                        )}
                        {appConfig.environment && (
                          <InfoRow
                            icon={<SettingOutlined />}
                            label="Entorno"
                            value={appConfig.environment}
                          />
                        )}
                        {description && (
                          <InfoRow
                            icon={<InfoCircleOutlined />}
                            label="Descripción"
                            value={description}
                          />
                        )}
                        <InfoRow
                          icon={<CloudUploadOutlined />}
                          label="Auto Deploy"
                          value={
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                              appConfig.autoDeploy
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            }`}>
                              {appConfig.autoDeploy ? '✓ Activado' : '— Desactivado'}
                            </span>
                          }
                        />
                        <InfoRow
                          icon={<CodeOutlined />}
                          label="Premium Logs"
                          value={
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                              appConfig.premiumLogs
                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            }`}>
                              {appConfig.premiumLogs
                                ? `✓ ${appConfig.premiumLogsPlan || 'Activado'}`
                                : '— Desactivado'}
                            </span>
                          }
                        />
                        {appType === 'server' && (service.apps?.length ?? 0) > 0 && (
                          <InfoRow
                            icon={<SettingOutlined />}
                            label="Apps"
                            value={`${service.apps?.length} app${(service.apps?.length ?? 0) !== 1 ? 's' : ''} configurada${(service.apps?.length ?? 0) !== 1 ? 's' : ''}`}
                          />
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {appType === 'static'
                          ? t('deployApp.detail.staticOverviewNotice')
                          : t('deployApp.detail.appsInService', { count: service.apps?.length ?? 0 })}
                      </p>
                      <button
                        type="button"
                        onClick={() => setActiveTab('apps')}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-2"
                      >
                        {appType === 'static' ? t('deployApp.detail.viewApp') : t('deployApp.detail.manageApps')}
                      </button>
                    </div>

                    {service.metadata?.technology === 'ai-generated' && service.publicUrl && (
                      <button
                        onClick={() => navigate(`/deploy-apps/${id}/editor`)}
                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <EditOutlined />
                        {t('deployApp.detail.openEditor')}
                      </button>
                    )}
                  </div>
                );
              })()}

              {activeTab === 'apps' && (
                <div className="min-h-[320px]">
                  {/* Header con descripción y botón agregar */}
                  {appType === 'server' && (
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('deployApp.detailApps.addAppDescription')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowAddAppModal(true)}
                        className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-1.5"
                      >
                        <PlusOutlined />
                        {t('deployApp.detailApps.addApp')}
                      </button>
                    </div>
                  )}
                  {appType === 'static' && (
                    <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 rounded-r-lg">
                      <p className="text-xs text-yellow-800 dark:text-yellow-200">
                        {t('deployApp.detailApps.staticSingleAppNotice')}
                      </p>
                    </div>
                  )}
                  {/* Tabs horizontales: una pestaña por app (solo para apps server con múltiples apps) */}
                  {appType !== 'static' && (
                    <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto pb-px mb-4">
                      {service.apps?.map((app) => (
                        <button
                          key={app.id}
                          type="button"
                          onClick={() => {
                            setSelectedAppId(app.id);
                            setActiveAppTab('overview');
                          }}
                          className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${selectedAppId === app.id
                            ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                        >
                          <span>{app.name || app.slug || app.id}</span>
                          {app.status && (
                            <ServiceStatusBadge
                              status={app.status.toLowerCase() as any}
                              size="sm"
                              showLabel={false}
                              showIcon={false}
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Contenido de la app seleccionada */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {selectedAppId && service.apps?.some((a) => a.id === selectedAppId) ? (
                      <>
                        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                          <div className="flex gap-1 overflow-x-auto min-w-0">
                            <button
                              type="button"
                              onClick={() => setActiveAppTab('overview')}
                              className={`flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium rounded transition-colors ${activeAppTab === 'overview'
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                              {t('deployApp.detail.overview')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveAppTab('configuration')}
                              className={`flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium rounded transition-colors ${activeAppTab === 'configuration'
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                              {t('deployApp.detail.configuration')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setActiveAppTab('deploys')}
                              className={`flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium rounded transition-colors ${activeAppTab === 'deploys'
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                              {t('deployApp.detail.deploys')}
                            </button>
                            {appType !== 'static' && (
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveAppTab('monitoring');
                                  setAppLogs('');
                                  setAppLogsError('');
                                }}
                                className={`flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium rounded transition-colors ${activeAppTab === 'monitoring'
                                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                  }`}
                              >
                                {t('deployApp.detail.monitoring')}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="p-4 sm:p-6">
                          {activeAppTab === 'overview' && (() => {
                            const app = service.apps!.find((a) => a.id === selectedAppId)!;
                            const appCreatedAt = app.createdAt ? (typeof app.createdAt === 'string' ? new Date(app.createdAt) : app.createdAt) : null;
                            const appUpdatedAt = app.updatedAt ? (typeof app.updatedAt === 'string' ? new Date(app.updatedAt) : app.updatedAt) : null;
                            const appLastDeployAt = app.lastDeployAt ? (typeof app.lastDeployAt === 'string' ? new Date(app.lastDeployAt) : app.lastDeployAt) : null;
                            return (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">App ID</label>
                                  <div className="flex items-center gap-2">
                                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{app.id}</code>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(app.id);
                                        success(t('deployApp.detail.appIdCopied'));
                                      }}
                                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                      title={t('deployApp.detail.copyId')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                                <div>
                                  <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.name')}</label>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{app.name}</p>
                                </div>
                                {app.slug && appType !== 'static' && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.slug')}</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{app.slug}</p>
                                  </div>
                                )}
                                {app.runtime && appType !== 'static' && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.runtime')}</label>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{app.runtime}</p>
                                  </div>
                                )}
                                {app.status && appType !== 'static' && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.status')}</label>
                                    <ServiceStatusBadge status={app.status.toLowerCase() as any} />
                                  </div>
                                )}
                                {appType !== 'static' && (
                                  <div className="col-span-1 sm:col-span-2">
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.process')}</label>
                                    {app.status?.toUpperCase() === 'STOPPED' ? (
                                      <div className="space-y-2">
                                        <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
                                          {t('deployApp.detail.appStoppedByUser')}
                                        </p>
                                        <button
                                          type="button"
                                          onClick={handleStartApp}
                                          disabled={isStartingApp}
                                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                        >
                                          <PlayCircleOutlined />
                                          {isStartingApp ? t('deployApp.detail.startingApp') : t('deployApp.detail.startApp')}
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex flex-wrap gap-2">
                                        <button
                                          type="button"
                                          onClick={() => setShowRestartAppModal(true)}
                                          disabled={isRestartingApp || isStoppingApp}
                                          className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                        >
                                          <ReloadOutlined className={isRestartingApp ? 'animate-spin' : ''} />
                                          {isRestartingApp ? t('deployApp.detail.restartingApp') : t('deployApp.detail.restartApp')}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setShowStopAppModal(true)}
                                          disabled={isRestartingApp || isStoppingApp}
                                          className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 border border-red-300 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                                        >
                                          <PauseCircleOutlined />
                                          {isStoppingApp ? t('deployApp.detail.stoppingApp') : t('deployApp.detail.stopApp')}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* {app.healthStatus && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Health</label>
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${app.healthStatus === 'HEALTHY' ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300' : app.healthStatus === 'UNHEALTHY' ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>{app.healthStatus}</span>
                                  </div>
                                )} */}
                                {appLastDeployAt && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.lastDeploy')}</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{appLastDeployAt.toLocaleString()}</p>
                                  </div>
                                )}
                                {app.lastDeployStatus && appType !== 'static' && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.deployStatus')}</label>
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${app.lastDeployStatus === 'SUCCESS' ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300' : app.lastDeployStatus === 'FAILED' ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>{app.lastDeployStatus}</span>
                                  </div>
                                )}
                                {appCreatedAt && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.created')}</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{getServiceAge(appCreatedAt)}</p>
                                  </div>
                                )}
                                {appUpdatedAt && (
                                  <div>
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.updated')}</label>
                                    <p className="text-sm text-gray-900 dark:text-white">{getServiceAge(appUpdatedAt)}</p>
                                  </div>
                                )}
                                {app.publicUrl && (
                                  <div className="col-span-1 sm:col-span-2">
                                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.publicUrl')}</label>
                                    <div className="flex items-center gap-2">
                                      <a
                                        href={app.publicUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                                      >
                                        <GlobalOutlined />
                                        {app.publicUrl}
                                      </a>
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(app.publicUrl!);
                                          success(t('deployApp.detail.urlCopied'));
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                        title={t('deployApp.detail.copyUrl')}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {appType === 'static' && (
                                  <div className="col-span-1 sm:col-span-2">
                                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                          <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {t('deployApp.detail.customDomainsTitle')}
                                          </h5>
                                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {t('deployApp.detail.customDomainsDescStatic')}
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={openDomainsConfiguration}
                                          className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors inline-flex items-center gap-2"
                                        >
                                          <GlobalOutlined />
                                          <span>{t('deployApp.detail.manageCustomDomains')}</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {activeAppTab === 'configuration' && (
                            <div ref={configMountRef} id="app-configuration-mount" className="space-y-6" />
                          )}
                          {activeAppTab === 'deploys' && (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.deployHistory')}</h4>
                                <button
                                  onClick={async () => {
                                    if (!selectedAppId) return;
                                    try {
                                      setIsLoadingDeployHistory(true);
                                      const response = await servicesService.getDeployHistory(selectedAppId, 10);
                                      setDeployHistory(response.deploys);
                                      setDeployHistoryNextToken(response.nextToken);
                                      setHasMoreDeploys(!!response.nextToken);
                                    } catch (err) {
                                      console.warn('Error refreshing deploy history:', err);
                                    } finally {
                                      setIsLoadingDeployHistory(false);
                                    }
                                  }}
                                  disabled={isLoadingDeployHistory}
                                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                  <ReloadOutlined className={isLoadingDeployHistory ? 'animate-spin' : ''} />
                                  {t('deployApp.detail.refresh')}
                                </button>
                              </div>

                              {isLoadingDeployHistory && deployHistory.length === 0 ? (
                                <div className="flex items-center justify-center py-12">
                                  <LoadingOutlined className="text-2xl text-blue-600" spin />
                                </div>
                              ) : deployHistory.length === 0 ? (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
                                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.noDeploysYet')}</p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {deployHistory.map((deploy) => (
                                    <div
                                      key={deploy.deployId}
                                      className={`border rounded-lg p-4 transition-colors ${deploy.isActive
                                        ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10'
                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                        }`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2 mb-1.5">
                                            {/* {getDeployStatusBadge(deploy.status)} */}
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium">
                                              {deploy.type === 'github' ? '🔗 GitHub' : '📦 CLI'}
                                            </span>
                                            {deploy.isActive && (
                                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 font-medium">
                                                {t('deployApp.detail.active')}
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {deploy.commitMessage || deploy.commit || deploy.releaseId || deploy.deployId}
                                          </p>
                                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                            {deploy.branch && (
                                              <span className="flex items-center gap-1">
                                                <CodeOutlined className="text-[10px]" />
                                                {deploy.branch}
                                              </span>
                                            )}
                                            {deploy.commitAuthor && <span>{t('deployApp.detail.by')} {deploy.commitAuthor}</span>}
                                            {deploy.env && (
                                              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{deploy.env}</span>
                                            )}
                                            {deploy.createdAt && <span>{new Date(deploy.createdAt).toLocaleString()}</span>}
                                          </div>
                                          {deploy.error && (
                                            <div className="mt-2 space-y-2">
                                              {(() => {
                                                // Check if error is LIVE_STORAGE_EXCEEDED
                                                try {
                                                  const errorObj = typeof deploy.error === 'string' ? JSON.parse(deploy.error) : deploy.error;
                                                  if (errorObj?.code === 'LIVE_STORAGE_EXCEEDED') {
                                                    return (
                                                      <DeployStorageWarning
                                                        isVisible={true}
                                                        siteSize={errorObj?.siteSize}
                                                        storageLimit={errorObj?.storageLimit}
                                                        className="mt-0"
                                                      />
                                                    );
                                                  }
                                                } catch {
                                                  // Not JSON, fall through to default error display
                                                }
                                                return (
                                                  <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300 truncate">
                                                    {deploy.error}
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                                          <code className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                                            {deploy.deployId.substring(0, 16)}...
                                          </code>
                                          {/* Rollback solo para deploys por CLI: los de GitHub no guardan
                                              artefacto en S3 (deployBucket/zip_key), así que el rollback por
                                              re-descarga de S3 no aplica. Se oculta para evitar un botón que
                                              siempre falla con 400. Fix backend pendiente (ver memoria). */}
                                          {deploy.type !== 'github' && (
                                            <button
                                              type="button"
                                              onClick={() => handleRollback(deploy)}
                                              disabled={rollingBackDeployId === deploy.deployId}
                                              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                            >
                                              {rollingBackDeployId === deploy.deployId ? (
                                                <>
                                                  <LoadingOutlined spin />
                                                  {t('deployApp.detail.rollbacking')}
                                                </>
                                              ) : (
                                                t('deployApp.detail.rollback')
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                  {hasMoreDeploys && (
                                    <button
                                      onClick={handleLoadMoreDeploys}
                                      disabled={isLoadingDeployHistory}
                                      className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                      {isLoadingDeployHistory ? (
                                        <><LoadingOutlined spin /><span>{t('deployApp.detail.loadingMore')}</span></>
                                      ) : (
                                        <span>{t('deployApp.detail.loadMoreDeploys')}</span>
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          {activeAppTab === 'monitoring' && (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.logs')}</h4>
                              </div>

                              {/* Controles */}
                              <div className="flex flex-wrap items-center gap-3 mb-4">
                                {/* Selector de tipo de log */}
                                <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                                  <button
                                    type="button"
                                    onClick={() => { setLogType('deploy'); setAppLogs(''); setAppLogsError(''); }}
                                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${logType === 'deploy'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                      }`}
                                  >
                                    {t('deployApp.detail.deploy')}
                                  </button>
                                  {appType !== 'static' && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => { setLogType('app'); setAppLogs(''); setAppLogsError(''); }}
                                        className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 dark:border-gray-600 transition-colors ${logType === 'app'
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                          }`}
                                      >
                                        {t('deployApp.detail.appLogs') || 'App Logs'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => { setLogType('app-error'); setAppLogs(''); setAppLogsError(''); }}
                                        className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 dark:border-gray-600 transition-colors ${logType === 'app-error'
                                          ? 'bg-red-600 text-white'
                                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                          }`}
                                      >
                                        {t('deployApp.detail.errors')}
                                      </button>
                                    </>
                                  )}
                                </div>

                                {/* Selector de líneas */}
                                <select
                                  value={logLines}
                                  onChange={(e) => setLogLines(parseInt(e.target.value))}
                                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                  <option value={50}>{t('deployApp.detail.lines', { count: 50 })}</option>
                                  <option value={100}>{t('deployApp.detail.lines', { count: 100 })}</option>
                                  <option value={200}>{t('deployApp.detail.lines', { count: 200 })}</option>
                                  <option value={500}>{t('deployApp.detail.lines', { count: 500 })}</option>
                                </select>

                                {/* Botón de cargar */}
                                <button
                                  onClick={() => handleFetchLogs()}
                                  disabled={isLoadingLogs}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                >
                                  {isLoadingLogs ? (
                                    <><LoadingOutlined spin className="text-[10px]" /><span>{t('deployApp.detail.loadingLogs')}</span></>
                                  ) : (
                                    <><ReloadOutlined className="text-[10px]" /><span>{t('deployApp.detail.loadLogs')}</span></>
                                  )}
                                </button>
                              </div>

                              {/* Error */}
                              {appLogsError && (
                                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                  <p className="text-sm text-red-700 dark:text-red-300">{appLogsError}</p>
                                </div>
                              )}

                              {/* Nota informativa para apps estáticas */}
                              {appType === 'static' && (
                                <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                  <p className="text-xs text-blue-700 dark:text-blue-300">
                                    {t('deployApp.detail.staticLogsNotice') || 'Las apps estáticas se sirven desde CloudFront/S3 y no tienen procesos PM2. Solo están disponibles los logs de despliegue.'}
                                  </p>
                                </div>
                              )}

                              {/* Contenido de logs */}
                              {!appLogs && !isLoadingLogs && !appLogsError ? (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                                    {t('deployApp.detail.selectLogType')}
                                  </p>
                                  <p className="text-xs text-gray-400 dark:text-gray-500">
                                    {t('deployApp.detail.logTypesDesc')}
                                  </p>
                                </div>
                              ) : (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                  <div className="bg-gray-900 dark:bg-black px-4 py-2 flex items-center justify-between">
                                    <span className="text-xs text-gray-400 font-mono">
                                      {logType === 'deploy'
                                        ? `deploy — ${selectedAppId}`
                                        : logType === 'app'
                                          ? `stdout — ${selectedAppId}`
                                          : `stderr — ${selectedAppId}`}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <label className="flex items-center gap-1.5 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={autoScrollLogs}
                                          onChange={(e) => setAutoScrollLogs(e.target.checked)}
                                          className="w-3 h-3 text-blue-600 border-gray-600 rounded"
                                        />
                                        <span className="text-xs text-gray-400">Auto-scroll</span>
                                      </label>
                                      <button
                                        onClick={() => {
                                          navigator.clipboard.writeText(appLogs);
                                          success(t('deployApp.detail.logsCopied'));
                                        }}
                                        className="text-xs text-gray-400 hover:text-white transition-colors px-2 py-1"
                                        title={t('deployApp.detail.copyLogs')}
                                      >
                                        {t('deployApp.detail.copy')}
                                      </button>
                                    </div>
                                  </div>
                                  <pre
                                    className="bg-gray-950 dark:bg-black text-green-400 text-xs font-mono p-4 overflow-auto max-h-[500px] whitespace-pre-wrap break-words"
                                    ref={(el) => {
                                      if (el && autoScrollLogs) {
                                        el.scrollTop = el.scrollHeight;
                                      }
                                    }}
                                  >
                                    {isLoadingLogs ? t('deployApp.detail.loadingLogsText') : appLogs}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {service.apps?.length ? t('deployApp.detailApps.selectAppTab') : t('deployApp.detailApps.noApps')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(activeTab === 'apps' && activeAppTab === 'configuration' && selectedAppId && configMountReady && configMountRef.current) && createPortal(
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.configuration')}</h3>
                  <div className="flex flex-col sm:flex-row gap-4 min-h-[400px]">
                    <nav className="w-full sm:w-48 flex-shrink-0 flex sm:block gap-1 sm:space-y-1 overflow-x-auto pb-2 sm:pb-0 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700 sm:pr-4">
                      {appType !== 'static' && (
                        <button
                          type="button"
                          onClick={() => setConfigSubTab('connectivity')}
                          className={`sm:w-full text-left flex-shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${configSubTab === 'connectivity' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                          {t('deployApp.detailApps.configConnectivity')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setConfigSubTab('apiKeys')}
                        className={`sm:w-full text-left flex-shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${configSubTab === 'apiKeys' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        {t('deployApp.detailApps.configApiKeys')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfigSubTab('envVars')}
                        className={`sm:w-full text-left flex-shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${configSubTab === 'envVars' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        {t('deployApp.detailApps.configEnvVars')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfigSubTab('domains')}
                        className={`sm:w-full text-left flex-shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${configSubTab === 'domains' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        {t('deployApp.detailApps.configDomains')}
                      </button>
                    </nav>
                    <div className="flex-1 min-w-0">
                      {configSubTab === 'envVars' && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.envVars')}</h4>
                            <div className="flex items-center gap-2">
                              {isEditingEnvVars && (
                                <>
                                  <button
                                    onClick={handleCancelEditEnvVars}
                                    disabled={isSavingEnvVars}
                                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {t('deployApp.detail.cancel')}
                                  </button>
                                  <button
                                    onClick={handleSaveEnvVars}
                                    disabled={isSavingEnvVars}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  >
                                    {isSavingEnvVars ? (
                                      <>
                                        <LoadingOutlined spin />
                                        <span>{t('deployApp.detail.saving')}</span>
                                      </>
                                    ) : (
                                      <>
                                        <SaveOutlined />
                                        <span>{t('deployApp.detail.save')}</span>
                                      </>
                                    )}
                                  </button>
                                </>
                              )}
                              {!isEditingEnvVars && (
                                <button
                                  onClick={() => setIsEditingEnvVars(true)}
                                  className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-2"
                                >
                                  <EditOutlined />
                                  <span>{t('deployApp.detail.edit')}</span>
                                </button>
                              )}
                            </div>
                          </div>
                          {environmentVariables.length > 0 || isEditingEnvVars ? (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                              {isEditingEnvVars ? (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {environmentVariables.map((envVar, index) => (
                                    <div key={index} className="p-4">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            {t('deployApp.detail.envVarName')}
                                          </label>
                                          <input
                                            type="text"
                                            value={envVar.name}
                                            onChange={(e) => handleUpdateEnvVar(index, 'name', e.target.value)}
                                            placeholder={t('deployApp.detail.envVarNamePlaceholder')}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            {t('deployApp.detail.envVarValue')}
                                          </label>
                                          <div className="relative">
                                            <input
                                              type={envVar.hidden ? 'password' : 'text'}
                                              value={envVar.value}
                                              onChange={(e) => handleUpdateEnvVar(index, 'value', e.target.value)}
                                              placeholder={t('deployApp.detail.envVarValuePlaceholder')}
                                              className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => handleUpdateEnvVar(index, 'hidden', !envVar.hidden)}
                                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                              title={envVar.hidden ? t('deployApp.detail.showValue') : t('deployApp.detail.hideValue')}
                                            >
                                              {envVar.hidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={envVar.hidden}
                                            onChange={(e) => handleUpdateEnvVar(index, 'hidden', e.target.checked)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
                                          />
                                          <span className="text-xs text-gray-600 dark:text-gray-400">
                                            {t('deployApp.detail.hideValue')}
                                          </span>
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveEnvVar(index)}
                                          className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                        >
                                          <DeleteOutlined />
                                          <span>{t('deployApp.detail.remove')}</span>
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                  <div className="p-4">
                                    <button
                                      type="button"
                                      onClick={handleAddEnvVar}
                                      className="w-full px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-2"
                                    >
                                      <PlusOutlined />
                                      <span>{t('deployApp.detail.addVariable')}</span>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {environmentVariables.map((envVar, index) => (
                                    <div key={index} className="px-4 py-3 flex justify-between items-center">
                                      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">{envVar.name}</span>
                                      <span className="text-sm text-gray-900 dark:text-white font-mono">
                                        {envVar.hidden ? '••••••••' : envVar.value}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('deployApp.detail.noEnvVars')}</p>
                              <button
                                onClick={handleAddEnvVar}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-2 mx-auto"
                              >
                                <PlusOutlined />
                                <span>Agregar Variable</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {configSubTab === 'connectivity' && (
                        <>
                          {appType === 'server' && (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.githubDeploy')}</h4>
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {t('deployApp.detail.connectRepoDesc')}
                                  </p>
                                </div>
                                {!githubConnected && (
                                  <button
                                    onClick={() => setShowGithubModal(true)}
                                    disabled={isConnectingGithub}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  >
                                    {isConnectingGithub ? (
                                      <>
                                        <LoadingOutlined spin />
                                        <span>{t('deployApp.detail.connecting')}</span>
                                      </>
                                    ) : (
                                      <>
                                        <CodeOutlined />
                                        <span>{t('deployApp.detail.connectGithub')}</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>

                              {githubConnected && selectedRepo ? (
                                // Repositorio conectado
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
                                  <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start gap-3">
                                      <div className="w-10 h-10 bg-gray-900 dark:bg-white rounded-lg flex items-center justify-center">
                                        <CodeOutlined className="text-white dark:text-gray-900 text-lg" />
                                      </div>
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                                          {selectedRepo.full_name || selectedRepo.name}
                                        </h5>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                          {t('deployApp.detail.branch')}: <span className="font-medium">{selectedBranch}</span>
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      onClick={handleDisconnectGithub}
                                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                    >
                                      {t('deployApp.detail.disconnect')}
                                    </button>
                                  </div>

                                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                      <CheckCircleOutlined className="text-green-600 dark:text-green-400 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-green-800 dark:text-green-200">
                                          {t('deployApp.detail.autoDeployActivated')}
                                        </p>
                                        <p className="text-xs text-green-700 dark:text-green-300 mt-1" dangerouslySetInnerHTML={{ __html: t('deployApp.detail.autoDeployDesc', { branch: selectedBranch }) }} />

                                      </div>
                                    </div>
                                  </div>

                                  {/* Estado del último deploy + acceso al historial unificado (pestaña Deploys,
                                      que incluye deploys por CLI y por push de GitHub). El ↻ refresca el estado
                                      vía /github/deploy/status (endpoint real). */}
                                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                      <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                        {t('deployApp.detail.recentDeploys')}
                                      </h6>
                                      <div className="flex items-center gap-2">
                                        {lastCheckedAt && (
                                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                            {t('deployApp.detail.lastChecked', { time: lastCheckedAt.toLocaleTimeString() })}
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          onClick={checkDeployStatus}
                                          disabled={isCheckingDeployStatus}
                                          title={t('deployApp.detail.updateStatus')}
                                          aria-label={t('deployApp.detail.updateStatus')}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          <ReloadOutlined className={isCheckingDeployStatus ? 'animate-spin' : ''} />
                                        </button>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setActiveAppTab('deploys')}
                                      className="w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                                    >
                                      <span>{t('deployApp.detail.viewDeployHistory')}</span>
                                      <span aria-hidden>→</span>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                // Sin repositorio conectado
                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
                                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                                    <CodeOutlined className="text-gray-400 dark:text-gray-500 text-2xl" />
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    {t('deployApp.detail.connectRepoEmpty')}
                                  </p>
                                  <button
                                    onClick={() => setShowGithubModal(true)}
                                    disabled={isConnectingGithub}
                                    className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                                  >
                                    {isConnectingGithub ? (
                                      <>
                                        <LoadingOutlined spin />
                                        <span>{t('deployApp.detail.connecting')}</span>
                                      </>
                                    ) : (
                                      <>
                                        <CodeOutlined />
                                        <span>{t('deployApp.detail.connectGithub')}</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {(githubConfig?.lastDeploy || githubConfig?.initialDeploy) && (
                            <div className="mt-6">
                              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                                {githubConfig?.lastDeploy ? t('deployApp.detail.lastDeployTitle') : t('deployApp.detail.initialDeployTitle')}
                              </h4>
                              <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
                                {(() => {
                                  // Usar lastDeploy si existe, sino initialDeploy
                                  const deploy = githubConfig?.lastDeploy || githubConfig?.initialDeploy;
                                  const currentStatus = deployStatus.status;

                                  return (
                                    <div className="space-y-3">
                                      {/* Estado */}
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.statusLabel')}</span>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${currentStatus === 'success'
                                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                          : currentStatus === 'failed'
                                            ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                            : currentStatus === 'in_progress'
                                              ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                              : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                                          }`}>
                                          {(currentStatus === 'pending' || currentStatus === 'in_progress') && (
                                            <LoadingOutlined spin />
                                          )}
                                          {currentStatus === 'success' && <CheckCircleOutlined />}
                                          {currentStatus === 'failed' && <CloseCircleOutlined />}
                                          {currentStatus === 'success' ? t('deployApp.detail.statusSuccess') :
                                            currentStatus === 'failed' ? t('deployApp.detail.statusFailed') :
                                              currentStatus === 'in_progress' ? t('deployApp.detail.statusInProgress') :
                                                currentStatus === 'pending' ? t('deployApp.detail.statusPending') : t('deployApp.detail.statusUnknown')}
                                        </span>
                                      </div>

                                      {/* Commit (solo para lastDeploy) */}
                                      {deploy?.commit && (
                                        <div className="flex items-start justify-between">
                                          <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.commit')}</span>
                                          <div className="text-right">
                                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">
                                              {deploy.commit}
                                            </code>
                                            {deploy.message && (
                                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 max-w-xs">
                                                {deploy.message}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Autor (solo para lastDeploy) */}
                                      {deploy?.author && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.author')}</span>
                                          <span className="text-sm text-gray-900 dark:text-white">
                                            {deploy.author}
                                          </span>
                                        </div>
                                      )}

                                      {/* Timestamp */}
                                      {deploy?.timestamp && (
                                        <div className="flex items-center justify-between">
                                          <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.date')}</span>
                                          <span className="text-sm text-gray-900 dark:text-white">
                                            {new Date(deploy.timestamp).toLocaleString()}
                                          </span>
                                        </div>
                                      )}

                                      {/* Output/Error (collapsible) */}
                                      {deployStatus.lastDeploy?.output && (
                                        <details className="mt-3">
                                          <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white">
                                            {t('deployApp.detail.viewDeployLogs')}
                                          </summary>
                                          <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-900 p-3 rounded overflow-x-auto max-h-64 overflow-y-auto">
                                            {deployStatus.lastDeploy.output}
                                          </pre>
                                        </details>
                                      )}

                                      {deployStatus.lastDeploy?.error && (
                                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                                          <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">{t('deployApp.detail.error')}</p>
                                          <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap">
                                            {deployStatus.lastDeploy.error}
                                          </pre>
                                        </div>
                                      )}

                                      {/* Mientras el deploy corre, se refresca solo (poll 5s). El refresco
                                          manual y la hora viven en el encabezado de "Últimos deploys". */}
                                      {(currentStatus === 'pending' || currentStatus === 'in_progress') && (
                                        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                                          <div className="w-full px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md flex items-center justify-center gap-2">
                                            <LoadingOutlined spin />
                                            <span>{t('deployApp.detail.autoUpdating')}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}

                          {/* Modal de GitHub Configuration */}
                          {showGithubModal && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                  {t('deployApp.detail.configureGithubDeploy')}
                                </h3>

                                {/* Step 1: Conectar GitHub */}
                                {!githubConnected ? (
                                  <div className="space-y-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                      <div className="flex items-start gap-3">
                                        <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-xl flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                                            {t('deployApp.detail.whatWillHappen')}
                                          </p>
                                          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                                            <li>{t('deployApp.detail.githubStep1')}</li>
                                            <li>{t('deployApp.detail.githubStep2')}</li>
                                            <li>{t('deployApp.detail.githubStep3')}</li>
                                            <li>{t('deployApp.detail.githubStep4')}</li>
                                          </ul>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-3">
                                      <button
                                        onClick={() => setShowGithubModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                      >
                                        {t('deployApp.detail.cancel')}
                                      </button>
                                      <button
                                        onClick={handleConnectGithub}
                                        disabled={isConnectingGithub}
                                        className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                      >
                                        {isConnectingGithub ? (
                                          <>
                                            <LoadingOutlined spin />
                                            <span>{t('deployApp.detail.connecting')}</span>
                                          </>
                                        ) : (
                                          <>
                                            <CodeOutlined />
                                            <span>{t('deployApp.detail.connectWithGithub')}</span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  // Step 2: Seleccionar repositorio y configurar
                                  <div className="space-y-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('deployApp.detail.selectRepo')}
                                      </label>
                                      <select
                                        value={selectedRepo?.id || ''}
                                        onChange={(e) => {
                                          const repo = githubRepos.find(r => r.id === e.target.value);
                                          setSelectedRepo(repo);
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                      >
                                        <option value="">{t('deployApp.detail.selectRepoPlaceholder')}</option>
                                        {githubRepos.map((repo) => (
                                          <option key={repo.id} value={repo.id}>
                                            {repo.full_name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {selectedRepo && (
                                      <>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            {t('deployApp.detail.branchToDeploy')}
                                          </label>
                                          <input
                                            type="text"
                                            value={selectedBranch}
                                            onChange={(e) => setSelectedBranch(e.target.value)}
                                            placeholder="main"
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          />
                                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            {t('deployApp.detail.branchAutoDeployHint')}
                                          </p>
                                        </div>

                                        {/* Start Mode */}
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            {t('deployApp.detail.startMode')}
                                          </label>
                                          <select
                                            value={githubStartMode}
                                            onChange={(e) => setGithubStartMode(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          >
                                            {hasStartModeDropdown ? (
                                              startModeOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                              ))
                                            ) : (
                                              <>
                                                <option value="npm">npm</option>
                                                <option value="node">node</option>
                                                <option value="python">python</option>
                                                <option value="python3">python3</option>
                                                <option value="gunicorn">gunicorn</option>
                                                <option value="uvicorn">uvicorn</option>
                                                <option value="go">go</option>
                                              </>
                                            )}
                                          </select>
                                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            {t('deployApp.detail.startModeHint')}
                                          </p>
                                        </div>

                                        {/* Target Mode */}
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            {t('deployApp.detail.targetFile')}
                                          </label>
                                          <input
                                            type="text"
                                            value={githubTargetMode}
                                            onChange={(e) => setGithubTargetMode(e.target.value)}
                                            placeholder={
                                              githubStartMode === 'npm' ? 'start' :
                                              githubStartMode === 'node' ? 'app.js' :
                                              githubStartMode === 'gunicorn' ? 'app:app' :
                                              githubStartMode === 'uvicorn' ? 'app:app' :
                                              githubStartMode === 'python' || githubStartMode === 'python3' ? 'app.py' :
                                              githubStartMode === 'dotnet' ? 'MyApp.dll' :
                                              githubStartMode === 'dotnet-run' ? 'src/Api/Api.csproj' :
                                              githubStartMode === 'publish' || githubStartMode === 'auto' ? 'src/Api/Api.csproj (opcional)' :
                                              githubStartMode === 'bin' ? './MyApp' :
                                              githubStartMode === 'go' ? 'main.go' :
                                              'start'
                                            }
                                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                          />
                                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            {githubStartMode === 'npm'
                                              ? t('deployApp.detail.targetFileNpm')
                                              : githubStartMode === 'gunicorn' || githubStartMode === 'uvicorn'
                                                ? (t('deployApp.detail.targetFilePython') || 'Ej: app:app, wsgi:application, main:app')
                                                : githubStartMode === 'python' || githubStartMode === 'python3'
                                                  ? (t('deployApp.detail.targetFilePythonScript') || 'Ej: app.py, main.py, manage.py')
                                                  : githubStartMode === 'dotnet'
                                                    ? (t('deployApp.detail.targetFileDotnet') || 'Ej: MyApp.dll (precompilado) o dejar vacío para auto-detectar')
                                                    : githubStartMode === 'dotnet-run'
                                                      ? 'Ruta al .csproj (modo dev, no recomendado en producción)'
                                                      : githubStartMode === 'publish' || githubStartMode === 'auto'
                                                        ? 'Ruta a un .csproj o .sln (opcional, se autodetecta). Hace dotnet publish y arranca el .dll'
                                                        : githubStartMode === 'bin'
                                                          ? 'Ruta al binario self-contained dentro del repo'
                                                          : githubStartMode === 'go'
                                                            ? (t('deployApp.detail.targetFileGo') || 'Ej: main.go, cmd/server/main.go')
                                                            : t('deployApp.detail.targetFileOther')}
                                          </p>
                                        </div>
                                      </>
                                    )}

                                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                      <button
                                        onClick={() => {
                                          setShowGithubModal(false);
                                          setSelectedRepo(null);
                                          setSelectedBranch('main');
                                        }}
                                        disabled={isConfiguringDeploy}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        {t('deployApp.detail.cancel')}
                                      </button>
                                      <button
                                        onClick={handleConfigureGithubDeploy}
                                        disabled={!selectedRepo || !selectedBranch || isConfiguringDeploy}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                      >
                                        {isConfiguringDeploy ? (
                                          <>
                                            <LoadingOutlined spin />
                                            <span>{t('deployApp.detail.configuring')}</span>
                                          </>
                                        ) : (
                                          <span>{t('deployApp.detail.activateAutoDeploy')}</span>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        </>
                      )}
                      {configSubTab === 'apiKeys' && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.apiKeys')}</h4>
                            <button
                              onClick={handleGenerateApiKey}
                              disabled={isGeneratingApiKey}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {isGeneratingApiKey ? (
                                <>
                                  <LoadingOutlined spin />
                                  <span>{t('deployApp.detail.generating')}</span>
                                </>
                              ) : (
                                <>
                                  <PlusOutlined />
                                  <span>{t('deployApp.detail.generateApiKey')}</span>
                                </>
                              )}
                            </button>
                          </div>

                          {!Array.isArray(apiKeys) || apiKeys.length === 0 ? (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('deployApp.detail.noApiKeys')}</p>
                              <button
                                onClick={handleGenerateApiKey}
                                disabled={isGeneratingApiKey}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                              >
                                {isGeneratingApiKey ? (
                                  <>
                                    <LoadingOutlined spin />
                                    <span>{t('deployApp.detail.generating')}</span>
                                  </>
                                ) : (
                                  <>
                                    <PlusOutlined />
                                    <span>{t('deployApp.detail.generateApiKey')}</span>
                                  </>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {Array.isArray(apiKeys) && apiKeys.map((apiKey) => {
                                const tokenInfo = apiKey.tokenInfo || {};
                                const keyCreatedAt = tokenInfo.createdAt
                                  ? (typeof tokenInfo.createdAt === 'string' ? new Date(tokenInfo.createdAt) : tokenInfo.createdAt)
                                  : null;
                                const expirationDate = formatExpirationDate(tokenInfo.expiresAt);
                                const isRevoked = tokenInfo.status === 'revoked' || tokenInfo.status === 'REVOKED';
                                const tokenId = tokenInfo.tokenId || '';

                                return (
                                  <div
                                    key={tokenId || apiKey.token}
                                    className={`p-4 ${isRevoked
                                      ? 'bg-gray-50 dark:bg-gray-800/50 opacity-60'
                                      : ''
                                      }`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1 space-y-2">
                                        {tokenInfo.name && (
                                          <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{tokenInfo.name}</p>
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                          <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">
                                            {maskApiKey(apiKey.token, tokenId)}
                                          </code>
                                          <button
                                            onClick={() => copyApiKey(apiKey.token, tokenId)}
                                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                            title={apiKey.token ? t('deployApp.detail.copyToken') : t('deployApp.detail.copyTokenId')}
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                          </button>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                          {keyCreatedAt && (
                                            <span>{t('deployApp.detail.createdAt')} {keyCreatedAt.toLocaleString()}</span>
                                          )}
                                          {expirationDate && (
                                            <span>{t('deployApp.detail.expiresAt')} {expirationDate}</span>
                                          )}
                                          {!expirationDate && tokenInfo.expiresAt === null && (
                                            <span className="text-green-600 dark:text-green-400">{t('deployApp.detail.noExpiration')}</span>
                                          )}
                                          <span className={`px-2 py-0.5 rounded ${isRevoked
                                            ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                            : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                            }`}>
                                            {isRevoked ? t('deployApp.detail.revoked') : t('deployApp.detail.activeStatus')}
                                          </span>
                                        </div>
                                      </div>
                                      {!isRevoked && (
                                        <button
                                          onClick={() => {
                                            setApiKeyToRevoke(tokenId);
                                            setShowRevokeModal(true);
                                          }}
                                          disabled={isRevokingApiKey === tokenId}
                                          className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {isRevokingApiKey === tokenId ? t('deployApp.detail.revoking') : t('deployApp.detail.revoke')}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      {configSubTab === 'domains' && (
                        <div className="space-y-4">
                          {/* Header */}
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-3">
                              <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                                {t('deployApp.detail.customDomainsTitle')}
                              </h4>
                              <button
                                type="button"
                                onClick={() => loadCustomDomains()}
                                disabled={isLoadingDomains}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ReloadOutlined className={isLoadingDomains ? 'animate-spin' : ''} />
                                <span>{t('deployApp.detail.refresh')}</span>
                              </button>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {appType === 'static'
                                ? t('deployApp.detail.customDomainsDescStatic')
                                : t('deployApp.detail.customDomainsDesc')}
                            </p>
                            {appType === 'static' && (() => {
                              const staticApp = service.apps?.find((a) => a.id === selectedAppId);
                              const defaultUrl = staticApp?.publicUrl || service.publicUrl;
                              if (!defaultUrl) return null;
                              return (
                                <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/40">
                                  <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                                    <InfoCircleOutlined className="mt-0.5 flex-shrink-0" />
                                    <span>
                                      {t('deployApp.detail.staticDefaultUrlHint')}{' '}
                                      <a
                                        href={defaultUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-mono underline hover:no-underline"
                                      >
                                        {defaultUrl}
                                      </a>
                                    </span>
                                  </p>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Add domain form — static apps: máximo 1 dominio */}
                          {appType === 'static' && visibleCustomDomains.length >= 1 ? (
                            <div className="p-4 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10">
                              <p className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
                                <InfoCircleOutlined className="mt-0.5 flex-shrink-0" />
                                <span>{t('deployApp.detail.staticDomainLimit')}</span>
                              </p>
                            </div>
                          ) : (
                          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4">
                            <label
                              htmlFor="customDomainInput"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                              {t('deployApp.detail.customDomainLabel')}
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <input
                                id="customDomainInput"
                                type="text"
                                value={newDomainInput}
                                onChange={(e) => handleDomainInputChange(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !isAddingDomain) {
                                    e.preventDefault();
                                    handleAddCustomDomain();
                                  }
                                }}
                                placeholder={t('deployApp.detail.customDomainPlaceholder')}
                                spellCheck={false}
                                autoCapitalize="off"
                                autoCorrect="off"
                                disabled={isAddingDomain || !selectedAppId}
                                aria-invalid={!!domainInputError}
                                className={`flex-1 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 ${
                                  domainInputError
                                    ? 'border-red-400 dark:border-red-500'
                                    : 'border-gray-300 dark:border-gray-600'
                                }`}
                              />
                              <button
                                type="button"
                                onClick={handleAddCustomDomain}
                                disabled={isAddingDomain || !newDomainInput.trim() || !selectedAppId}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                              >
                                {isAddingDomain ? (
                                  <>
                                    <LoadingOutlined spin />
                                    <span>{t('deployApp.detail.adding')}</span>
                                  </>
                                ) : (
                                  <>
                                    <PlusOutlined />
                                    <span>{t('deployApp.detail.addCustomDomain')}</span>
                                  </>
                                )}
                              </button>
                            </div>
                            {domainInputError ? (
                              <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                                <CloseCircleOutlined className="mt-0.5" />
                                <span>{t(`deployApp.detail.${domainInputError}`)}</span>
                              </p>
                            ) : (
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {appType === 'static'
                                  ? t('deployApp.detail.customDomainHintStatic')
                                  : t('deployApp.detail.customDomainHint')}
                              </p>
                            )}
                          </div>
                          )}

                          {/* List of domains */}
                          {isLoadingDomains && visibleCustomDomains.length === 0 ? (
                            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                              <LoadingOutlined spin className="mr-2" />
                              <span className="text-sm">{t('deployApp.detail.loadingLogs')}</span>
                            </div>
                          ) : visibleCustomDomains.length === 0 ? (
                            <div className="border border-dashed border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-8 text-center">
                              <GlobalOutlined className="text-3xl text-gray-300 dark:text-gray-600 mb-3" />
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('deployApp.detail.noCustomDomains')}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {appType === 'static'
                                  ? t('deployApp.detail.noCustomDomainsHintStatic')
                                  : t('deployApp.detail.noCustomDomainsHint')}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {visibleCustomDomains.map((domain) => {
                                const isVerifying = verifyingDomainId === domain.domainId;

                                // -------- Status badge ----------
                                const statusUpper = (domain.status || '').toUpperCase();
                                const statusKey =
                                  statusUpper === 'ACTIVE'
                                    ? 'domainStatusActive'
                                    : statusUpper === 'FAILED'
                                      ? 'domainStatusFailed'
                                      : statusUpper === 'PENDING_TRAFFIC_DNS'
                                        ? 'domainStatusPendingTrafficDns'
                                        : statusUpper === 'PROVISIONING'
                                          ? 'domainStatusProvisioning'
                                          : statusUpper === 'VALIDATING'
                                            ? 'domainStatusValidating'
                                            : statusUpper === 'WAITING_DNS'
                                              ? 'domainStatusWaitingDns'
                                              : 'domainStatusPending';
                                const statusClasses =
                                  statusUpper === 'ACTIVE'
                                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                    : statusUpper === 'FAILED'
                                      ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                      : statusUpper === 'PROVISIONING' || statusUpper === 'VALIDATING'
                                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                        : statusUpper === 'PENDING_TRAFFIC_DNS'
                                          ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                          : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300';
                                const statusIcon =
                                  statusUpper === 'ACTIVE' ? (
                                    <CheckCircleOutlined />
                                  ) : statusUpper === 'FAILED' ? (
                                    <CloseCircleOutlined />
                                  ) : (
                                    <ClockCircleOutlined />
                                  );

                                // -------- Certificate status badge ----------
                                const certUpper = (domain.certificateStatus || '').toUpperCase();
                                const certKey =
                                  certUpper === 'ISSUED'
                                    ? 'certStatusIssued'
                                    : certUpper === 'PENDING_VALIDATION'
                                      ? 'certStatusPendingValidation'
                                      : certUpper === 'FAILED'
                                        ? 'certStatusFailed'
                                        : certUpper === 'REVOKED'
                                          ? 'certStatusRevoked'
                                          : '';
                                const certClasses =
                                  certUpper === 'ISSUED'
                                    ? 'bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/40'
                                    : certUpper === 'FAILED' || certUpper === 'REVOKED'
                                      ? 'bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40'
                                      : 'bg-yellow-50 dark:bg-yellow-900/10 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800/40';

                                // -------- DNS records ----------
                                const trafficCname = domain.dnsInstructions?.traffic;
                                const certValCname = domain.dnsInstructions?.certificateValidation;

                                // -------- Dates ----------
                                const createdDate = domain.createdAt
                                  ? typeof domain.createdAt === 'string'
                                    ? new Date(domain.createdAt)
                                    : domain.createdAt
                                  : null;
                                const updatedDate = domain.updatedAt
                                  ? typeof domain.updatedAt === 'string'
                                    ? new Date(domain.updatedAt)
                                    : domain.updatedAt
                                  : null;

                                // -------- Helper to render a single DNS row ----------
                                const renderDnsRecord = (
                                  record: { type: string; name: string; value: string } | undefined,
                                  fieldKeyBase: string
                                ) => {
                                  if (!record) {
                                    return (
                                      <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                                        {t('deployApp.detail.dnsRecordMissing')}
                                      </p>
                                    );
                                  }
                                  const nameFieldId = `${domain.domainId}-${fieldKeyBase}-name`;
                                  const valueFieldId = `${domain.domainId}-${fieldKeyBase}-value`;
                                  return (
                                    <div className="grid grid-cols-1 sm:grid-cols-[80px_1fr_1fr] gap-3 text-xs">
                                      <div className="space-y-1">
                                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                                          {t('deployApp.detail.cnameType')}
                                        </p>
                                        <div className="px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm">
                                          {record.type || 'CNAME'}
                                        </div>
                                      </div>
                                      <div className="space-y-1 min-w-0">
                                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                                          {t('deployApp.detail.cnameName')}
                                        </p>
                                        <div className="flex items-stretch">
                                          <code className="flex-1 px-3 py-2 rounded-l-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm overflow-x-auto whitespace-nowrap">
                                            {record.name}
                                          </code>
                                          <button
                                            type="button"
                                            onClick={() => handleCopyDomainField(record.name, nameFieldId)}
                                            className="px-3 rounded-r-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 transition-colors flex items-center"
                                            title={t('deployApp.detail.copyValue')}
                                          >
                                            {copiedField === nameFieldId ? (
                                              <span className="text-xs font-medium">{t('deployApp.detail.copied')}</span>
                                            ) : (
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                              </svg>
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                      <div className="space-y-1 min-w-0">
                                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                                          {t('deployApp.detail.cnameValue')}
                                        </p>
                                        <div className="flex items-stretch">
                                          <code className="flex-1 px-3 py-2 rounded-l-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm overflow-x-auto whitespace-nowrap">
                                            {record.value}
                                          </code>
                                          <button
                                            type="button"
                                            onClick={() => handleCopyDomainField(record.value, valueFieldId)}
                                            className="px-3 rounded-r-md bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 transition-colors flex items-center"
                                            title={t('deployApp.detail.copyValue')}
                                          >
                                            {copiedField === valueFieldId ? (
                                              <span className="text-xs font-medium">{t('deployApp.detail.copied')}</span>
                                            ) : (
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                              </svg>
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                };

                                const isCertIssued = certUpper === 'ISSUED';
                                const isStaticDomainFlow = appType === 'static';
                                const showStaticCertStep = isStaticDomainFlow && !isCertIssued && statusUpper !== 'ACTIVE';
                                const showStaticTrafficStep = isStaticDomainFlow && isCertIssued && statusUpper !== 'ACTIVE';
                                const showCertDnsSection = isStaticDomainFlow ? showStaticCertStep : true;
                                const showTrafficDnsSection = isStaticDomainFlow ? showStaticTrafficStep : true;

                                return (
                                  <div
                                    key={domain.domainId}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden"
                                  >
                                    {/* Header */}
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                      <div className="flex items-start gap-3 min-w-0">
                                        <GlobalOutlined className="text-blue-500 text-lg flex-shrink-0 mt-0.5" />
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white break-all">
                                            {domain.domainName}
                                          </p>
                                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                            <span
                                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${statusClasses}`}
                                            >
                                              {statusIcon}
                                              <span>{t(`deployApp.detail.${statusKey}`)}</span>
                                            </span>
                                            {certKey && (
                                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${certClasses}`}>
                                                <span className="text-[10px] uppercase tracking-wide opacity-75">{t('deployApp.detail.certStatusLabel')}:</span>
                                                <span>{t(`deployApp.detail.${certKey}`)}</span>
                                              </span>
                                            )}
                                            {createdDate && (
                                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {t('deployApp.detail.domainAddedAt')}: {createdDate.toLocaleString()}
                                              </span>
                                            )}
                                            {updatedDate && statusUpper === 'ACTIVE' && (
                                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {t('deployApp.detail.domainUpdatedAt')}: {updatedDate.toLocaleString()}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center lg:justify-end gap-2 w-full lg:w-auto lg:flex-shrink-0">
                                        {statusUpper === 'ACTIVE' ? (
                                          <a
                                            href={`https://${domain.domainName}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full lg:w-auto justify-center px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors inline-flex items-center gap-2 shadow-sm whitespace-nowrap"
                                            title={t('deployApp.detail.openSiteHint')}
                                          >
                                            <ExportOutlined />
                                            <span>{t('deployApp.detail.openSite')}</span>
                                          </a>
                                        ) : showStaticCertStep ? (
                                          <button
                                            type="button"
                                            onClick={() => handleVerifyCustomDomain(domain)}
                                            disabled={isVerifying}
                                            className="w-full lg:w-auto justify-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-sm whitespace-nowrap"
                                            title={t('deployApp.detail.validateCertificateHint')}
                                          >
                                            {isVerifying ? (
                                              <>
                                                <LoadingOutlined spin />
                                                <span>{t('deployApp.detail.validatingCertificate')}</span>
                                              </>
                                            ) : (
                                              <>
                                                <SafetyCertificateOutlined />
                                                <span>{t('deployApp.detail.validateCertificate')}</span>
                                              </>
                                            )}
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => handleVerifyCustomDomain(domain)}
                                            disabled={isVerifying}
                                            className="w-full lg:w-auto justify-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-sm whitespace-nowrap"
                                            title={t('deployApp.detail.verifyDnsHint')}
                                          >
                                            {isVerifying ? (
                                              <>
                                                <LoadingOutlined spin />
                                                <span>{t('deployApp.detail.verifyingDns')}</span>
                                              </>
                                            ) : (
                                              <>
                                                <ReloadOutlined />
                                                <span>{t('deployApp.detail.verifyDns')}</span>
                                              </>
                                            )}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => handleRequestDeleteDomain(domain)}
                                          className="w-full lg:w-auto justify-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
                                          title={t('deployApp.detail.deleteDomain')}
                                          aria-label={t('deployApp.detail.deleteDomain')}
                                        >
                                          <DeleteOutlined />
                                          <span>{t('deployApp.detail.deleteDomain')}</span>
                                        </button>
                                      </div>
                                    </div>

                                    {/* Body */}
                                    <div className="px-4 sm:px-5 py-4 space-y-5">
                                      {/* Mensaje del backend (ej: "Configure both DNS records and then click Verify Domain.") */}
                                      {domain.message && statusUpper !== 'ACTIVE' && (
                                        <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/40">
                                          <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                                            <InfoCircleOutlined className="mt-0.5 flex-shrink-0" />
                                            <span>{domain.message}</span>
                                          </p>
                                        </div>
                                      )}

                                      {(showCertDnsSection || showTrafficDnsSection) && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          {showStaticCertStep
                                            ? t('deployApp.detail.staticCertDnsInstructions')
                                            : showStaticTrafficStep
                                              ? t('deployApp.detail.staticTrafficDnsInstructions')
                                              : t('deployApp.detail.dnsInstructions')}
                                        </p>
                                      )}

                                      {/* Traffic CNAME — server siempre; static solo tras certificado emitido */}
                                      {showTrafficDnsSection && (
                                      <div className="space-y-2">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                          <div className="flex items-center gap-2">
                                            <GlobalOutlined className="text-blue-500" />
                                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                                              {t('deployApp.detail.dnsSectionTraffic')}
                                            </h5>
                                          </div>
                                          {domain.targetDomain && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              {t('deployApp.detail.dnsTargetDomain')}:{' '}
                                              <code className="font-mono text-gray-700 dark:text-gray-300">{domain.targetDomain}</code>
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          {t('deployApp.detail.dnsSectionTrafficDesc')}
                                        </p>
                                        {renderDnsRecord(trafficCname, 'traffic')}
                                      </div>
                                      )}

                                      {/* Certificate Validation CNAME — server siempre; static solo en paso 1 */}
                                      {showCertDnsSection && (
                                      <div className="space-y-2">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                          <div className="flex items-center gap-2">
                                            <InfoCircleOutlined className="text-yellow-500" />
                                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                                              {t('deployApp.detail.dnsSectionCertValidation')}
                                            </h5>
                                          </div>
                                          {isCertIssued && !isStaticDomainFlow && (
                                            <span className="text-xs text-green-700 dark:text-green-400 inline-flex items-center gap-1">
                                              <CheckCircleOutlined />
                                              {t('deployApp.detail.dnsSectionCertValidationDoneShort')}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          {isCertIssued && !isStaticDomainFlow
                                            ? t('deployApp.detail.dnsSectionCertValidationDone')
                                            : t('deployApp.detail.dnsSectionCertValidationDesc')}
                                        </p>
                                        {renderDnsRecord(certValCname, 'certval')}
                                      </div>
                                      )}

                                      {isStaticDomainFlow && statusUpper === 'ACTIVE' && (
                                        <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/40">
                                          <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                                            <CheckCircleOutlined className="mt-0.5 flex-shrink-0" />
                                            <span>{t('deployApp.detail.staticDomainActiveHint')}</span>
                                          </p>
                                        </div>
                                      )}

                                      {statusUpper === 'FAILED' && domain.errorMessage && (
                                        <div className="mt-2 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
                                          <p className="text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                                            <CloseCircleOutlined className="mt-0.5 flex-shrink-0" />
                                            <span>{domain.errorMessage}</span>
                                          </p>
                                        </div>
                                      )}

                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  </div>
                </div>,
                configMountRef.current
              )}

              {activeTab === 'events' && appType === 'server' && (
                <LazyServiceEventsList serviceId={id || ''} eventsPerPage={10} />
              )}

              {activeTab === 'monitoring' && appType === 'server' && (
                <MonitoringTab service={service as any} />
              )}

              {activeTab === 'backups' && appType === 'server' && (
                <BackupsTab serviceId={id || ''} />
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="lg:col-span-4 space-y-4 sm:space-y-6">
          {/* Status Card */}
          <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.statusCard')}</h3>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.status')}</span>
                <ServiceStatusBadge status={service.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.created')}</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">{t('deployApp.detail.updated')}</span>
                <span className="text-sm text-gray-900 dark:text-white">{getServiceAge(service.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Connection Card - Solo para server apps running */}
          {appType === 'server' && service.status === 'running' && (
            <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.serverConnection')}</h3>
              </div>
              <div className="p-6 space-y-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('deployApp.detail.serverConnectionDesc')}
                </p>
                <button
                  onClick={() => navigate(`/deploy-apps/terminal/${id}`)}
                  className="w-full px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <CodeOutlined />
                  {t('deployApp.detail.connectToServer')}
                </button>
                <div className="text-center items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mt-2">
                  <span>{t('virtualMachines.detail.connection.secureAccess')}</span>
                  <button
                    onClick={() => setShowSecurityHelpModal(true)}
                    className="ml-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title={t('virtualMachines.detail.connection.secureAccess')}
                  >
                    <InfoCircleOutlined className="text-xs" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Product Card */}
          {product && (
            <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.product')}</h3>
              </div>
              <div className="p-6 space-y-3">
                {product.name && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.name')}</label>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{product.name}</p>
                  </div>
                )}
                {product.description && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{t('deployApp.detail.description')}</label>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{product.description}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ZIP Upload Card - Solo para apps estáticas */}
          {appType === 'static' && (
            <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.deploySite')}</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {t('deployApp.detail.deploySiteDesc')}
                </p>

                {/* Input hidden */}
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setZipFile(file);
                      setUploadPhase('idle');
                      setUploadError('');
                    }
                  }}
                />

                {/* Zona de drop */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setZipDragActive(true); }}
                  onDragLeave={() => setZipDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setZipDragActive(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.name.endsWith('.zip')) {
                      setZipFile(file);
                      setUploadPhase('idle');
                      setUploadError('');
                    } else {
                      showError(t('deployApp.detail.onlyZipFiles'));
                    }
                  }}
                  onClick={() => !isUploadingZip && zipInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isUploadingZip ? 'cursor-not-allowed opacity-60' :
                    zipDragActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                      : zipFile
                        ? 'border-green-400 bg-green-50 dark:bg-green-900/10 cursor-pointer'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer'
                  }`}
                >
                  <CloudUploadOutlined className={`text-3xl mb-2 ${
                    zipDragActive ? 'text-blue-500' : zipFile ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'
                  }`} />
                  {zipFile ? (
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{zipFile.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {(zipFile.size / 1024 / 1024).toFixed(2)} MB
                        {!isUploadingZip && ` — ${t('deployApp.detail.clickToChange')}`}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('deployApp.detail.dragZipHere')}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('deployApp.detail.onlyZip')}</p>
                    </div>
                  )}
                </div>

                {/* Barra de progreso */}
                {uploadPhase !== 'idle' && uploadPhase !== 'done' && uploadPhase !== 'error' && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 dark:text-gray-400">
                        {uploadPhase === 'requesting' && t('deployApp.detail.preparingDeploy')}
                        {uploadPhase === 'uploading' && t('deployApp.detail.uploadingFile', { progress: uploadProgress })}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all duration-300 bg-blue-600"
                        style={{
                          width: uploadPhase === 'requesting' ? '10%'
                            : `${Math.max(uploadProgress, 10)}%`
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Resultado exitoso */}
                {uploadPhase === 'done' && (
                  <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircleOutlined className="text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-green-800 dark:text-green-200">
                        {t('deployApp.detail.deployUploaded')}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error */}
                {uploadPhase === 'error' && uploadError && (
                  <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CloseCircleOutlined className="text-red-600 dark:text-red-400" />
                      <span className="text-sm text-red-800 dark:text-red-200">{uploadError}</span>
                    </div>
                  </div>
                )}

                {/* Botones de acción */}
                {zipFile && uploadPhase !== 'done' && (
                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={handleStaticZipDeploy}
                      disabled={isUploadingZip || !selectedAppId}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isUploadingZip ? (
                        <><LoadingOutlined spin /> {t('deployApp.detail.deploying')}</>
                      ) : (
                        <><CloudUploadOutlined /> {t('deployApp.detail.deployBtn')}</>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setZipFile(null);
                        setUploadPhase('idle');
                        setUploadError('');
                        setUploadProgress(0);
                      }}
                      disabled={isUploadingZip}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      {t('deployApp.detail.cancel')}
                    </button>
                  </div>
                )}

                {/* Aviso si no hay app seleccionada */}
                {!selectedAppId && appType === 'static' && (
                  <p className="mt-3 text-xs text-yellow-600 dark:text-yellow-400">
                    {t('deployApp.detail.selectAppBeforeDeploy')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Custom Domains - Solo para apps estáticas */}
          {appType === 'static' && (
            <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('deployApp.detail.customDomainsTitle')}</h3>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('deployApp.detail.customDomainsDescStatic')}
                </p>
                <button
                  type="button"
                  onClick={openDomainsConfiguration}
                  disabled={!selectedAppId}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <GlobalOutlined />
                  <span>{t('deployApp.detail.manageCustomDomains')}</span>
                </button>
              </div>
            </div>
          )}

          {/* Documentación */}
          {(() => {
            const techLower = technology?.toLowerCase() || '';
            const runtimeSlug =
              techLower.includes('node') ? 'nodejs' :
              techLower.includes('python') ? 'python' :
              techLower === 'go' || techLower === 'golang' ? 'go' :
              techLower.includes('dotnet') || techLower.includes('.net') || techLower.includes('netcore') ? 'dotnet' :
              null;
            const docsUrl = runtimeSlug
              ? `https://docs.kubiy.com/app-deploy/${runtimeSlug}`
              : 'https://docs.kubiy.com/app-deploy';
            return (
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documentación</h3>
                <a
                  href={docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-3 py-2 bg-white dark:bg-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span>Ver documentación</span>
                  <ExportOutlined className="text-xs" />
                </a>
              </div>
            );
          })()}
        </div>
      </div>


      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('deployApp.detail.deleteApp')}
        message={t('deployApp.detail.deleteAppConfirm', { name: service.name })}
        confirmText={t('deployApp.detail.delete')}
        cancelText={t('deployApp.detail.cancel')}
        variant="danger"
        loading={isDeleting}
      />

      {/* Restart App Process Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRestartAppModal}
        onClose={() => { if (!isRestartingApp) setShowRestartAppModal(false); }}
        onConfirm={handleRestartApp}
        title={t('deployApp.detail.restartApp')}
        message={t('deployApp.detail.restartAppConfirm')}
        confirmText={t('deployApp.detail.restartApp')}
        cancelText={t('deployApp.detail.cancel')}
        variant="default"
        loading={isRestartingApp}
      />

      {/* Stop App Process Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStopAppModal}
        onClose={() => { if (!isStoppingApp) setShowStopAppModal(false); }}
        onConfirm={handleStopApp}
        title={t('deployApp.detail.stopApp')}
        message={t('deployApp.detail.stopAppConfirm')}
        confirmText={t('deployApp.detail.stopApp')}
        cancelText={t('deployApp.detail.cancel')}
        variant="danger"
        loading={isStoppingApp}
      />

      {/* Revoke API Key Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setApiKeyToRevoke(null);
        }}
        onConfirm={handleRevokeApiKey}
        title={t('deployApp.detail.revokeApiKey')}
        message={t('deployApp.detail.revokeApiKeyConfirm')}
        confirmText={t('deployApp.detail.revoke')}
        cancelText={t('deployApp.detail.cancel')}
        variant="danger"
        loading={!!isRevokingApiKey}
      />

      {/* Delete Custom Domain Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDomainModal}
        onClose={() => {
          if (!isDeletingDomain) {
            setShowDeleteDomainModal(false);
            setDomainToDelete(null);
          }
        }}
        onConfirm={handleConfirmDeleteDomain}
        title={t('deployApp.detail.deleteDomain')}
        message={t('deployApp.detail.deleteDomainConfirm', { domain: domainToDelete?.domainName ?? '' })}
        confirmText={t('deployApp.detail.deleteDomain')}
        cancelText={t('deployApp.detail.cancel')}
        variant="danger"
        loading={isDeletingDomain}
      />

      {/* Modal: Cambiar plan */}
      {showChangePlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Cambiar plan</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              El servicio tendrá <span className="font-medium text-yellow-600 dark:text-yellow-400">~2-3 min de downtime</span> mientras EC2 cambia de tipo de instancia.
            </p>
            {(() => {
              const PLAN_OPTIONS = [
                { plan: 'dev-assigned-nano',  label: 'Nano',       instance: 't3.nano',    vcpu: 2, memGB: 0.5 },
                { plan: 'dev-assigned-micro', label: 'Micro',      instance: 't3.micro',   vcpu: 2, memGB: 1   },
                { plan: 'dev-assigned-small', label: 'Small',      instance: 't3.small',   vcpu: 2, memGB: 2   },
                { plan: 'KY_EDGE',            label: 'Edge',        instance: 't3.small',   vcpu: 2, memGB: 2   },
                { plan: 'KY_CORE_STANDARD',   label: 'Core',        instance: 't3.micro',   vcpu: 2, memGB: 1   },
                { plan: 'dedicated-staging',  label: 'Staging',     instance: 't3.micro',   vcpu: 2, memGB: 1   },
                { plan: 'dedicated-production', label: 'Production', instance: 't3.medium', vcpu: 2, memGB: 4  },
                { plan: 'KY_FORGE',           label: 'Forge',       instance: 't3.xlarge',  vcpu: 4, memGB: 16  },
              ];
              const currentInstance = (service as any).instanceType as string | undefined;
              return (
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {PLAN_OPTIONS.map((opt) => {
                    const isCurrent = opt.instance === currentInstance;
                    const isSelected = selectedNewPlan === opt.plan;
                    return (
                      <button
                        key={opt.plan}
                        type="button"
                        disabled={isCurrent}
                        onClick={() => setSelectedNewPlan(opt.plan)}
                        className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                          isCurrent
                            ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : isSelected
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-white'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="font-medium">{opt.label}</span>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">{opt.instance}</span>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{opt.vcpu} vCPU · {opt.memGB} GB RAM</span>
                        {isCurrent && <span className="ml-2 text-xs text-blue-500 dark:text-blue-400">(actual)</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {changePlanError && (
              <p className="text-sm text-red-500 dark:text-red-400 mb-3">{changePlanError}</p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setShowChangePlanModal(false); setSelectedNewPlan(''); setChangePlanError(null); }}
                disabled={isChangingPlan}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleChangePlan}
                disabled={!selectedNewPlan || isChangingPlan}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isChangingPlan && <LoadingOutlined className="text-sm" />}
                {isChangingPlan ? 'Aplicando...' : 'Cambiar plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Limit Modal - MAX_RELEASES_REACHED */}
      <DeployReleaseDeleteModal
        isOpen={showStorageLimitModal}
        onClose={() => setShowStorageLimitModal(false)}
        onDeleteSuccess={handleStorageLimitModalDeleteSuccess}
        currentReleases={storageLimitData.currentReleases}
        maxReleases={storageLimitData.maxReleases}
        oldestRelease={storageLimitData.oldestRelease}
      />

      {/* Modal para solicitar nombre de API Key */}
      {showApiKeyNameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('deployApp.detail.generateApiKeyTitle')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('deployApp.detail.apiKeyNameLabel')}
                </label>
                <input
                  type="text"
                  value={apiKeyName}
                  onChange={(e) => setApiKeyName(e.target.value)}
                  placeholder={t('deployApp.detail.apiKeyNamePlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && apiKeyName.trim()) {
                      handleConfirmGenerateApiKey();
                    }
                  }}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('deployApp.detail.apiKeyNameHint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('deployApp.detail.expirationDays')}
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={apiKeyUnlimited}
                      onChange={(e) => {
                        setApiKeyUnlimited(e.target.checked);
                        if (e.target.checked) {
                          setApiKeyExpirationDays('');
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('deployApp.detail.unlimited')}</span>
                  </label>
                  {!apiKeyUnlimited && (
                    <div>
                      <input
                        type="number"
                        value={apiKeyExpirationDays}
                        onChange={(e) => setApiKeyExpirationDays(e.target.value)}
                        placeholder={t('deployApp.detail.expirationPlaceholder')}
                        min="1"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('deployApp.detail.expirationHint')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowApiKeyNameModal(false);
                  setApiKeyName('');
                  setApiKeyExpirationDays('');
                  setApiKeyUnlimited(false);
                }}
                disabled={isGeneratingApiKey}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('deployApp.detail.cancel')}
              </button>
              <button
                onClick={handleConfirmGenerateApiKey}
                disabled={isGeneratingApiKey || !apiKeyName.trim() || (!apiKeyUnlimited && apiKeyExpirationDays !== '' && (isNaN(parseInt(apiKeyExpirationDays)) || parseInt(apiKeyExpirationDays) < 1))}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isGeneratingApiKey ? (
                  <>
                    <LoadingOutlined spin />
                    <span>{t('deployApp.detail.generating')}</span>
                  </>
                ) : (
                  <span>{t('deployApp.detail.generate')}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para mostrar el token generado */}
      {showTokenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('deployApp.detail.apiKeyGenerated')}
            </h3>

            {/* Advertencias */}
            <div className="mb-6 space-y-3">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <InfoCircleOutlined className="text-yellow-600 dark:text-yellow-400 text-xl flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                      {t('deployApp.detail.tokenWarningTitle')}
                    </p>
                    <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 list-disc list-inside">
                      <li dangerouslySetInnerHTML={{ __html: t('deployApp.detail.tokenWarning1') }} />
                      <li dangerouslySetInnerHTML={{ __html: t('deployApp.detail.tokenWarning2') }} />
                      <li>{t('deployApp.detail.tokenWarning3')}</li>
                      <li>{t('deployApp.detail.tokenWarning4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Token */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('deployApp.detail.yourApiKey')}
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-4 py-3 text-sm bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md font-mono text-gray-900 dark:text-white break-all">
                  {generatedToken}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedToken);
                    success(t('deployApp.detail.tokenCopied'));
                  }}
                  className="px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-2 flex-shrink-0"
                  title={t('deployApp.detail.copyToken')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>{t('deployApp.detail.copy')}</span>
                </button>
              </div>
            </div>

            {/* Información adicional del token */}
            {generatedTokenInfo && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('deployApp.detail.tokenInfo')}</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {generatedTokenInfo.name && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('deployApp.detail.tokenName')}</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{generatedTokenInfo.name}</span>
                    </div>
                  )}
                  {generatedTokenInfo.scope && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('deployApp.detail.tokenScope')}</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">{generatedTokenInfo.scope}</span>
                    </div>
                  )}
                  {generatedTokenInfo.createdAt && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('deployApp.detail.tokenCreated')}</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {typeof generatedTokenInfo.createdAt === 'string'
                          ? new Date(generatedTokenInfo.createdAt).toLocaleString()
                          : generatedTokenInfo.createdAt.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {generatedTokenInfo.expiresAt ? (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('deployApp.detail.tokenExpires')}</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {formatExpirationDate(generatedTokenInfo.expiresAt)}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('deployApp.detail.tokenExpiration')}</span>
                      <span className="ml-2 text-green-600 dark:text-green-400 font-medium">{t('deployApp.detail.tokenUnlimited')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Botón de cerrar */}
            <div className="flex items-center justify-end">
              <button
                onClick={() => {
                  setShowTokenModal(false);
                  setGeneratedToken('');
                  setGeneratedTokenInfo(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                {t('deployApp.detail.tokenUnderstood')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar app */}
      {showAddAppModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('deployApp.detail.addNewApp')}
            </h3>

            <div className="space-y-4">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('deployApp.detail.appName')}
                </label>
                <input
                  type="text"
                  value={newAppData.name}
                  onChange={(e) => setNewAppData({ ...newAppData, name: e.target.value })}
                  placeholder={t('deployApp.detail.appNamePlaceholder')}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('deployApp.detail.appDescription')}
                </label>
                <textarea
                  value={newAppData.description}
                  onChange={(e) => setNewAppData({ ...newAppData, description: e.target.value })}
                  placeholder={t('deployApp.detail.appDescPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
              </div>

              {/* Slug o dominio de la app */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('deployApp.detail.appSlugLabel')} *
                </label>
                <input
                  type="text"
                  value={newAppData.slug}
                  onChange={(e) => setNewAppData({ ...newAppData, slug: e.target.value.replace(/\s/g, '').toLowerCase() })}
                  placeholder={t('deployApp.detail.appSlugPlaceholder')}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('deployApp.detail.appSlugUrlPreview')}{' '}
                  <span className="font-mono text-blue-600 dark:text-blue-400 break-all">
                    https://{(newAppData.slug || 'slug').trim() || 'slug'}.{(service?.metadata?.appConfig?.subdomain ?? '').trim() || 'subdomain'}.apps1.kubiy.site
                  </span>
                </p>
                {(service?.apps?.length ?? 0) > 0 && (() => {
                  const existingSlugsOrNames = (service?.apps || []).map((a: any) => [(a.name || '').trim().toLowerCase(), (a.slug || '').trim().toLowerCase()].filter(Boolean)).flat();
                  const slugDuplicado = (newAppData.slug ?? '').trim() && existingSlugsOrNames.includes((newAppData.slug ?? '').trim().toLowerCase());
                  return slugDuplicado ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {t('deployApp.detail.appSlugDuplicate')}
                    </p>
                  ) : null;
                })()}
              </div>

              {/* Ambiente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('deployApp.detail.appEnvironment')}
                </label>
                <input
                  type="text"
                  value={newAppData.environment}
                  onChange={(e) => setNewAppData({ ...newAppData, environment: e.target.value })}
                  placeholder={t('deployApp.detail.appEnvPlaceholder')}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('deployApp.detail.appEnvHint')}
                </p>
              </div>

              {/* Modo de Inicio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('deployApp.detail.startModeLabel')}
                </label>
                {hasStartModeDropdown ? (
                  <select
                    value={newAppData.startMode}
                    onChange={(e) => setNewAppData({ ...newAppData, startMode: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('deployApp.detail.selectStartMode')}</option>
                    {startModeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={newAppData.startMode}
                    onChange={(e) => setNewAppData({ ...newAppData, startMode: e.target.value })}
                    placeholder={t('deployApp.detail.startModePlaceholder')}
                    required
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {hasStartModeDropdown
                    ? t('deployApp.detail.startModeDropdownHint', { tech: technology })
                    : t('deployApp.detail.startModeInputHint')}
                </p>
              </div>

              {/* Archivo Objetivo */}
              {(() => {
                const tech = (service?.metadata?.technology || service?.metadata?.appConfig?.type || '').toString().toLowerCase();
                const isNetcore = tech.includes('netcore') || tech.includes('dotnet') || tech.includes('.net');
                const sm = (newAppData.startMode || '').trim();
                const isTargetOptional = isNetcore && (sm === 'auto' || sm === 'publish');
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('deployApp.detail.targetFileLabel')}
                      {isTargetOptional && (
                        <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">({t('deployApp.step2.targetFile.optional')})</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={newAppData.targetMode}
                      onChange={(e) => setNewAppData({ ...newAppData, targetMode: e.target.value })}
                      placeholder={t('deployApp.detail.targetFilePlaceholder')}
                      required={!isTargetOptional}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t('deployApp.detail.targetFileHint')}
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Botones */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAddAppModal(false);
                  setNewAppData({
                    name: '',
                    description: '',
                    slug: '',
                    environment: '',
                    startMode: '',
                    targetMode: '',
                  });
                }}
                disabled={isCreatingApp}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('deployApp.detail.cancel')}
              </button>
              <button
                onClick={handleCreateApp}
                disabled={isCreatingApp || !(service?.metadata?.appConfig?.subdomain ?? '').trim() || !newAppData.name.trim() || !(newAppData.slug ?? '').trim() || !newAppData.environment.trim() || !newAppData.startMode.trim() || (() => {
                  const tech = (service?.metadata?.technology || service?.metadata?.appConfig?.type || '').toString().toLowerCase();
                  const isNetcore = tech.includes('netcore') || tech.includes('dotnet') || tech.includes('.net');
                  const sm = newAppData.startMode.trim();
                  const isTargetOptional = isNetcore && (sm === 'auto' || sm === 'publish');
                  if (!isTargetOptional && !newAppData.targetMode.trim()) return true;
                  const existingSlugsOrNames = (service?.apps || []).map((a: any) => [(a.name || '').trim().toLowerCase(), (a.slug || '').trim().toLowerCase()].filter(Boolean)).flat();
                  return existingSlugsOrNames.includes((newAppData.slug ?? '').trim().toLowerCase());
                })()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isCreatingApp ? (
                  <>
                    <LoadingOutlined spin />
                    <span>{t('deployApp.detail.creating')}</span>
                  </>
                ) : (
                  <span>{t('deployApp.detail.createApp')}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Help Modal - Acceso Seguro por Defecto */}
      {showSecurityHelpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-dark-bg rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('virtualMachines.detail.connection.secureAccessInfo')}</h2>
                <button
                  onClick={() => setShowSecurityHelpModal(false)}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  <CloseCircleOutlined className="text-xl" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
                <p className="text-sm text-blue-900 dark:text-blue-300">
                  <strong>{t('virtualMachines.detail.connection.secureAccessDesc')}</strong>
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('virtualMachines.detail.connection.secureAccessInfo')}</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  {t('virtualMachines.detail.connection.secureAccessInfoDesc')}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowSecurityHelpModal(false)}
                className="px-6 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeployAppDetail;

